import React from "react";
import i18next from "i18next";
import { withRouter } from "react-router-dom";
import { connect } from "react-redux";
import moment from "moment";
import {
  FormGroup,
  FormControl,
  FormLabel,
  InputGroup,
  Tab,
  OverlayTrigger,
  Tooltip,
} from "react-bootstrap";
import { toastr } from "react-redux-toastr";
import SimpleModal from "components/Modal/SimpleModal";
import SubmitButton from "components/SubmitButton";
import Switch from "components/Switch";
import { accountsService, employeesService, iPlanService } from "services";
import * as calendarsActions from "actions/calendarsActions";
import { getCachedAvatar, saveCachedAvatar } from "utils/avatarCache";
import { FiX, FiInfo } from "react-icons/fi";
import { FaMagic } from "react-icons/fa";
import { getFirstLastInterval, convertFromUTC } from "utils/datetime";
import {
  calcAssigmentsStats,
  errorFormating,
  formatNumber,
  isValidDecimalInput,
  normalizeDecimal,
  renderErrorList,
  sanitizeDecimalInput,
} from "utils/utils";
import TimeConfigModal from "./TimeConfigModal";
import TimeInputWithArrows from "components/TimeInputWithArrows";
import TargetHoursOverridesNotice from "./TargetHoursOverridesNotice/TargetHoursOverridesNotice";
import PillToggleGroup from "components/PillToggleGroup";
import "./TimeConfigModal.scss";
import "./PlanMagicModalV2.scss";
import {
  isBalanceShiftsHighPriorityVisible,
  resolveBalanceShiftsHighPriority,
  withBalanceShiftsByEmployee,
} from "./planMagicBalanceShifts";
import {
  FRACTION_SHIFT_OPTIONS,
  stepFractionShiftValue,
} from "./planMagicFractionShifts";
import {
  createDefaultMagicPlan,
  mergeSavedPlanningParams,
  normalizeRestWindowBetweenBlocks,
  readLocalPlanningParams,
} from "./planMagicSavedParams";

const TIME_LIMIT_OPTIONS = [
  { value: 60, labelKey: "magicPlan.time1min" },
  { value: 300, labelKey: "magicPlan.time5min", recommended: true },
  { value: 1800, labelKey: "magicPlan.time30min" },
  { value: 3600, labelKey: "magicPlan.time1h" },
  { value: 7200, labelKey: "magicPlan.time2h" },
  { value: 14400, labelKey: "magicPlan.time4h" },
  { value: 43200, labelKey: "magicPlan.time12h" },
  { value: 86400, labelKey: "magicPlan.time1d" },
  { value: 172800, labelKey: "magicPlan.time2d" },
  { value: 259200, labelKey: "magicPlan.time3d" },
  { value: 432000, labelKey: "magicPlan.time5d" },
];
const RECOMMENDED_TIMELIMIT = 300;

const DECIMAL_RESTRICTION_FIELDS = new Set([
  "max_working_hours_between_restdays",
  "minimum_daily_hours",
  "maximum_daily_hours",
  "minimum_working_hours_per_block",
  "maximum_working_hours_per_block",
  "minimum_rest_hours_between_blocks",
  "maximum_rest_hours_between_blocks",
  "min_resthours_between_workdays",
  "min_resthours_between_shifts",
  "max_resthours_between_shifts",
  "minimum_weekly_consecutive_rest_hours",
  "min_consecutive_rest_hours",
]);

/**
 * PlanMagicModalV2 - Modal de planificación con IA
 *
 * La plantilla de restricciones es del grupo: el POST de lanzamiento la guarda
 * y el último lanzamiento pisa al anterior. Al abrir se lee
 * GET /intelligent-planning/params/{groupId}.
 * start/end salen del periodo abierto. Las horas objetivo solo vuelven si ese
 * lanzamiento es del mismo intervalo. Un 404 con
 * intelligent-planning.params_not_found usa la copia del navegador de ese
 * grupo si existe, y si no, los valores por defecto.
 */
class PlanMagicModalV2 extends React.Component {
  constructor(props) {
    super(props);

    const magic_plan = createDefaultMagicPlan({
      start: props.start || "",
      end: props.end || "",
      groupId: props.group_id || "",
      timelimit: RECOMMENDED_TIMELIMIT,
    });

    this.state = {
      isLoading: false,
      submitted: false,
      errors: {},
      error: "",
      error_footer: "",
      magic_plan,
      new_day_type: {},
      selected_day_types: [],
      new_shift: {},
      selected_shifts: [],
      new_high_load_shift: {},
      selected_high_load_shifts: [],
      shift_weekly_limits: [],
      new_shift_weekly_limit: { shift_id: "", max_weekly_shift_assignments: "" },
      show_time_modal: false,
      active_modal_type: null,
      temp_time_config: { start: "", end: "" },
      temp_time_config_friday: { start: "", end: "" },
      configurationLoaded: false,
      loadedForGroupId: null, // Rastrear para qué grupo se cargó la configuración
      objectiveAssignments: [],
      balanceAssignments: [],
      assignmentsLoading: false,
      assignmentsLoaded: false,
      activeTabKey: "config",
      employeeAvatars: {},
      avatarEmployeesSignature: "",
      targetHoursSort: { key: null, direction: "asc" },
    };

    this.assignmentsRequestToken = 0;
    this.paramsRequestId = 0;
    this.lastAppliedParams = null;
    this.lastAppliedGroupId = null;
    this.targetHoursDebugSignature = "";

    // Referencias para los inputs
    this.inputRefs = {
      "min_resthours_between_workdays.value": React.createRef(),
      "min_resthours_between_shifts.value": React.createRef(),
      "max_resthours_between_shifts.value": React.createRef(),
      "min_weekly_restdays.value": React.createRef(),
      "minimum_free_weekends.value": React.createRef(),
      "minimum_weekly_consecutive_rest_hours.value": React.createRef(),
      "min_consecutive_restdays.value": React.createRef(),
      "min_consecutive_rest_hours.value": React.createRef(),
      "max_consecutive_restdays.value": React.createRef(),
      "minimum_working_hours_per_block.value": React.createRef(),
      "maximum_working_hours_per_block.value": React.createRef(),
      "minimum_rest_hours_between_blocks.value": React.createRef(),
      "maximum_rest_hours_between_blocks.value": React.createRef(),
      "number_of_blocks.value": React.createRef(),
      "max_weekly_days_with_multiple_blocks.value": React.createRef(),
      "min_consecutive_workdays.value": React.createRef(),
      "max_working_hours_between_restdays.value": React.createRef(),
      "minimum_daily_hours.value": React.createRef(),
      "maximum_daily_hours.value": React.createRef(),
      "max_consecutive_same_shift.value": React.createRef(),
    };
  }

  componentDidMount() {
    this.props.getSelectableDayTypes();
    this.loadSavedParams();
  }

  componentWillUnmount() {
    this.paramsRequestId += 1;
  }

  componentDidUpdate(prevProps, prevState) {
    const currentGroupId = this.props.group_id;
    const prevGroupId = prevProps.group_id;
    const groupIdChanged = currentGroupId !== prevGroupId;
    const needsInitialLoad = !this.state.configurationLoaded && !prevState.configurationLoaded;

    if (groupIdChanged || needsInitialLoad) {
      this.loadSavedParams();
    }

    this.hydrateSavedSelectionsWhenCatalogsArrive(prevProps);

    const currentPlanGroupId = this.state.magic_plan?.group_id;
    const currentStart = this.state.magic_plan?.start;
    const currentEnd = this.state.magic_plan?.end;
    const prevGroupIdFromState = prevState.magic_plan?.group_id;
    const prevStartFromState = prevState.magic_plan?.start;
    const prevEndFromState = prevState.magic_plan?.end;
    const shouldReloadAssignments = (
      currentPlanGroupId !== prevGroupIdFromState
      || currentStart !== prevStartFromState
      || currentEnd !== prevEndFromState
    );

    if (shouldReloadAssignments) {
      this.targetHoursDataKey = null;
      if (this.state.activeTabKey === "horas-objetivo") {
        this.ensureTargetHoursData();
      }
    }

    if (this.state.activeTabKey === "horas-objetivo") {
      this.preloadEmployeeAvatars();
      this.logTargetHoursDiagnostics();
    }
  }

  catalogsReady = (props) => (
    (props.select_day_types?.length || 0) > 0
    && (props.attendance_shifts?.length || 0) > 0
  );

  buildStateFromSavedParams = (params, groupId) => {
    const merged = mergeSavedPlanningParams({
      defaults: createDefaultMagicPlan({
        start: this.props.start || "",
        end: this.props.end || "",
        groupId,
        timelimit: RECOMMENDED_TIMELIMIT,
      }),
      params,
      start: this.props.start || "",
      end: this.props.end || "",
      groupId,
      dayTypes: this.props.select_day_types || [],
      shifts: this.props.attendance_shifts || [],
      recommendedTimelimit: RECOMMENDED_TIMELIMIT,
    });

    return {
      magic_plan: {
        ...merged.magic_plan,
        employees: this.normalizeTargetHoursEmployees(merged.magic_plan.employees),
      },
      selected_day_types: merged.selected_day_types,
      selected_shifts: merged.selected_shifts,
      selected_high_load_shifts: merged.selected_high_load_shifts,
      shift_weekly_limits: merged.shift_weekly_limits,
    };
  };

  hydrateSavedSelectionsWhenCatalogsArrive = (prevProps) => {
    if (!this.catalogsReady(this.props) || this.catalogsReady(prevProps)) return;
    if (!this.lastAppliedParams) return;
    if (String(this.lastAppliedGroupId) !== String(this.props.group_id)) return;
    if (!this.state.configurationLoaded) return;

    const hydrated = this.buildStateFromSavedParams(this.lastAppliedParams, this.props.group_id);
    this.setState({
      selected_day_types: hydrated.selected_day_types,
      selected_shifts: hydrated.selected_shifts,
      selected_high_load_shifts: hydrated.selected_high_load_shifts,
      shift_weekly_limits: hydrated.shift_weekly_limits,
    });
  };

  loadSavedParams = () => {
    const groupId = this.props.group_id;
    if (groupId === undefined || groupId === null || groupId === "") {
      if (!this.state.configurationLoaded) {
        this.setState({ configurationLoaded: true, loadedForGroupId: null });
      }
      return;
    }

    if (
      this.paramsLoad
      && String(this.paramsLoad.groupId) === String(groupId)
      && this.paramsLoad.requestId === this.paramsRequestId
    ) {
      return;
    }

    const requestId = (this.paramsRequestId || 0) + 1;
    this.paramsRequestId = requestId;
    this.paramsLoad = { groupId, requestId };
    this.lastAppliedParams = null;
    this.lastAppliedGroupId = groupId;

    this.setState({
      configurationLoaded: false,
      loadedForGroupId: null,
      magic_plan: createDefaultMagicPlan({
        start: this.props.start || "",
        end: this.props.end || "",
        groupId,
        timelimit: RECOMMENDED_TIMELIMIT,
      }),
      selected_day_types: [],
      selected_shifts: [],
      selected_high_load_shifts: [],
      shift_weekly_limits: [],
    });

    this.fetchSavedParams(groupId, requestId);
  };

  fetchSavedParams = async (groupId, requestId) => {
    let response;
    try {
      response = await iPlanService.getPlanningParams(groupId);
    } catch (error) {
      if (this.paramsRequestId !== requestId) return;
      this.finishParamsLoad(groupId, null, "error", requestId);
      return;
    }

    if (this.paramsRequestId !== requestId) return;
    if (String(this.props.group_id) !== String(groupId)) return;

    if (
      response?.ok
      && response.params
      && typeof response.params === "object"
      && !Array.isArray(response.params)
    ) {
      this.finishParamsLoad(groupId, response.params, "ok", requestId);
      return;
    }

    if (response?.status === 404) {
      this.finishParamsLoad(groupId, null, "empty", requestId);
      return;
    }

    if (response?.status === 403) {
      this.finishParamsLoad(groupId, null, "forbidden", requestId);
      return;
    }

    this.finishParamsLoad(groupId, null, "error", requestId);
  };

  localPlanningStorage = () => {
    try {
      return window.localStorage;
    } catch (error) {
      return null;
    }
  };

  finishParamsLoad = (groupId, params, outcome, requestId) => {
    if (this.paramsRequestId !== requestId) return;
    if (String(this.props.group_id) !== String(groupId)) return;

    if (outcome === "forbidden") {
      toastr.error(i18next.t("common.error"), i18next.t("magicPlan.paramsForbidden"));
      this.lastAppliedParams = null;
      this.lastAppliedGroupId = groupId;
      this.setState({
        magic_plan: createDefaultMagicPlan({
          start: this.props.start || "",
          end: this.props.end || "",
          groupId,
          timelimit: RECOMMENDED_TIMELIMIT,
        }),
        selected_day_types: [],
        selected_shifts: [],
        selected_high_load_shifts: [],
        shift_weekly_limits: [],
        configurationLoaded: false,
        loadedForGroupId: groupId,
      });
      return;
    }

    if (outcome === "error") {
      toastr.error(i18next.t("common.error"), i18next.t("magicPlan.paramsLoadError"));
    }

    if (!params && outcome === "empty") {
      params = readLocalPlanningParams(this.localPlanningStorage(), groupId);
    }

    if (params) {
      this.lastAppliedParams = params;
      this.lastAppliedGroupId = groupId;
      this.setState({
        ...this.buildStateFromSavedParams(params, groupId),
        configurationLoaded: true,
        loadedForGroupId: groupId,
      });
      return;
    }

    this.lastAppliedParams = null;
    this.lastAppliedGroupId = groupId;
    this.setState({
      magic_plan: createDefaultMagicPlan({
        start: this.props.start || "",
        end: this.props.end || "",
        groupId,
        timelimit: RECOMMENDED_TIMELIMIT,
      }),
      selected_day_types: [],
      selected_shifts: [],
      selected_high_load_shifts: [],
      shift_weekly_limits: [],
      configurationLoaded: true,
      loadedForGroupId: groupId,
    });
  };

  onDatesChange = (dates) => {
    var { startDate, endDate } = dates;
    const { magic_plan } = this.state;
    if (startDate) startDate = startDate.format("YYYY-MM-DD");
    if (endDate) endDate = endDate.format("YYYY-MM-DD");

    const prevStart = magic_plan.start;
    const prevEnd = magic_plan.end;

    let updatedMagicPlan = { ...magic_plan, start: startDate, end: endDate };

    const hadFullRange = Boolean(prevStart && prevEnd);
    const hasFullRange = Boolean(updatedMagicPlan.start && updatedMagicPlan.end);
    const intervalChanged = hadFullRange && hasFullRange
      && (prevStart !== updatedMagicPlan.start || prevEnd !== updatedMagicPlan.end);

    if (intervalChanged) {
      updatedMagicPlan = { ...updatedMagicPlan, employees: [] };
    }

    const persistMagicPlan = (nextPlan) => {
      this.setState({ magic_plan: nextPlan });
    };

    // Si el período cambió, validar que la opción de bloques seleccionada siga siendo válida
    if (updatedMagicPlan.iterative_calculation_in_days.active) {
      const tempMagicPlan = { ...updatedMagicPlan };
      const blockValue = tempMagicPlan.iterative_calculation_in_days.value;
      let isValid;

      if (blockValue === 27) {
        isValid = this.isCompleteFourWeeksPeriodWithDates(tempMagicPlan.start, tempMagicPlan.end);
      } else {
        isValid = this.isCompleteWeeksPeriodWithDates(tempMagicPlan.start, tempMagicPlan.end);
      }

      if (!isValid) {
        tempMagicPlan.iterative_calculation_in_days = { active: false, value: 0 };
      }
      persistMagicPlan(tempMagicPlan);
    } else {
      persistMagicPlan(updatedMagicPlan);
    }
  };

  // Método auxiliar para validar semanas completas con fechas específicas
  isCompleteWeeksPeriodWithDates = (startDate, endDate) => {
    if (!startDate || !endDate) return false;

    const start = moment(startDate);
    const end = moment(endDate);

    // Verificar que la fecha de inicio sea lunes (día 1 en moment.js)
    const isStartMonday = start.day() === 1;

    // Verificar que la fecha de fin sea domingo (día 0 en moment.js)
    const isEndSunday = end.day() === 0;

    // Verificar que la diferencia en días sea múltiplo de 7 (semanas completas)
    const daysDifference = end.diff(start, 'days');
    const isCompleteWeeks = daysDifference >= 6 && (daysDifference + 1) % 7 === 0;

    return isStartMonday && isEndSunday && isCompleteWeeks;
  };

  // Método auxiliar para validar bloques de 4 semanas completas con fechas específicas
  isCompleteFourWeeksPeriodWithDates = (startDate, endDate) => {
    if (!startDate || !endDate) return false;

    const start = moment(startDate);
    const end = moment(endDate);

    const isStartMonday = start.day() === 1;
    const isEndSunday = end.day() === 0;

    // Verificar que la diferencia en días sea múltiplo de 28 (bloques de 4 semanas)
    const daysDifference = end.diff(start, 'days');
    const isCompleteFourWeeks = daysDifference >= 27 && (daysDifference + 1) % 28 === 0;

    return isStartMonday && isEndSunday && isCompleteFourWeeks;
  };

  // Manejar el cambio del select "Cálculo por bloques"
  handleIterativeBlockChange = (selectValue) => {
    const { magic_plan } = this.state;
    const value = typeof selectValue === "string"
      ? selectValue
      : selectValue?.target?.value;

    if (value === "6" && !this.isCompleteWeeksPeriod()) {
      toastr.info(i18next.t('magicPlan.iterativeWeeklyTitle'), i18next.t('magicPlan.iterativeBlockHintWeekly'));
      return;
    }

    if (value === "27" && !this.isCompleteFourWeeksPeriod()) {
      toastr.info(i18next.t('magicPlan.iterativeFourWeeksTitle'), i18next.t('magicPlan.iterativeBlockHintFourWeeks'));
      return;
    }

    if (value === "0") {
      magic_plan.iterative_calculation_in_days = { active: false, value: 0 };
    } else {
      magic_plan.iterative_calculation_in_days = { active: true, value: parseInt(value, 10) };
    }

    this.setState({ magic_plan });
  };

  getRestrictionTabOptions = (magic_plan) => {
    const options = [
      { value: "horas", label: i18next.t("magicPlan.tabHours") },
    ];

    if (magic_plan.fraction_shifts) {
      options.push({
        value: "fracciones",
        label: i18next.t("magicPlan.tabFractions"),
      });
    }

    options.push(
      { value: "descansos", label: i18next.t("magicPlan.tabRests") },
      { value: "ubicaciones", label: i18next.t("magicPlan.tabFunctionsUbications") },
      { value: "horas-objetivo", label: i18next.t("magicPlan.targetHoursLabel") },
    );

    return options;
  };

  contentScrollRef = React.createRef();

  resetContentScroll = () => {
    const node = this.contentScrollRef.current;
    if (node) node.scrollTop = 0;
  };

  handleTabSelect = (activeTabKey) => {
    if (!activeTabKey) return;

    const isLeavingTargetHoursTab = this.state.activeTabKey === "horas-objetivo"
      && activeTabKey !== "horas-objetivo";

    if (isLeavingTargetHoursTab) {
      this.commitClearedTargetHoursOverrides(() => {
        this.setState({ activeTabKey }, this.resetContentScroll);
      });
      return;
    }

    this.setState({ activeTabKey }, () => {
      this.resetContentScroll();
      if (activeTabKey === "horas-objetivo") {
        this.ensureTargetHoursData();
      }
    });
  };

  ensureTargetHoursData = () => {
    const groupId = parseInt(this.state.magic_plan?.group_id || this.props.group_id, 10);
    const planningStart = this.state.magic_plan?.start || this.props.start;
    const planningEnd = this.state.magic_plan?.end || this.props.end;
    const key = `${groupId || ""}|${planningStart || ""}|${planningEnd || ""}`;

    if (this.targetHoursDataKey === key) return;
    this.targetHoursDataKey = key;

    this.loadAccountAssignmentsData();
    if (typeof this.props.loadAssignmentsGraphics === "function") {
      this.props.loadAssignmentsGraphics();
    }
  };

  getSelectedGroup = () => {
    const groupId = parseInt(this.state.magic_plan?.group_id || this.props.group_id, 10);
    const group = this.props.group;
    if (group && parseInt(group.id, 10) === groupId) return group;

    const detailedGroups = Array.isArray(this.props.detailed_groups) ? this.props.detailed_groups : [];
    return detailedGroups.find((item) => parseInt(item.id, 10) === groupId) || null;
  };

  isDecimalRestrictionField = (fieldKey) => DECIMAL_RESTRICTION_FIELDS.has(fieldKey);

  parseRestrictionDecimalValue = (value) => {
    if (value === "" || value === null || value === undefined) return value;
    const parsed = Number(normalizeDecimal(value));
    return Number.isFinite(parsed) ? parsed : value;
  };

  normalizeMagicPlanDecimalValues = (plan) => {
    const normalized = { ...plan };
    DECIMAL_RESTRICTION_FIELDS.forEach((field) => {
      if (!normalized[field]) return;
      const config = normalized[field];
      if (config.value === "" || config.value === null || config.value === undefined) return;
      normalized[field] = {
        ...config,
        value: this.parseRestrictionDecimalValue(config.value),
      };
    });
    return normalized;
  };

  handleChange = (event) => {
    const { magic_plan } = this.state;
    const key = event.target.id;
    let value = event.target.value;

    // Manejar diferentes tipos de valores
    if (value === "on") {
      value = event.target.checked;
    } else if (value === "") {
      // Mantener cadena vacía para selects sin selección
      value = "";
    } else if (key.indexOf(".") > -1) {
      const [fieldKey, fieldProp] = key.split(".");
      if (fieldProp === "value" && this.isDecimalRestrictionField(fieldKey)) {
        if (!isValidDecimalInput(value)) return;
        value = sanitizeDecimalInput(value);
      } else if (!isNaN(value)) {
        value = value * 1;
      }
    } else if (!isNaN(value) && value !== "") {
      value = value * 1;
    }

    if (key.indexOf(".") > -1) {
      const [k0, k1] = key.split(".");
      magic_plan[k0][k1] = value;


      // Si se activó un switch, poner foco en el input correspondiente
      if (k1 === "active" && value === true) {
        const inputKey = `${k0}.value`;
        setTimeout(() => {
          if (this.inputRefs[inputKey] && this.inputRefs[inputKey].current) {
            this.inputRefs[inputKey].current.focus();
            this.inputRefs[inputKey].current.select();
          }
        }, 100);
      }

      // Si se desactivó un switch, limpiar el valor del input asociado
      if (k1 === "active" && value === false && magic_plan[k0].hasOwnProperty('value')) {
        if (k0 === "rest_window_between_blocks") {
          magic_plan[k0].value = { start: "", end: "" };
        } else {
          magic_plan[k0].value = 0;
        }
      }
    } else if (key === "extra_hours_allowed" && value) {
      magic_plan.extra_hours_allowed = value;
      magic_plan.over_assign_needs = false;
    } else if (key === "over_assign_needs" && value) {
      magic_plan.over_assign_needs = value;
      magic_plan.extra_hours_allowed = false;
    } else if (key === "forbid_unassigned_shifts" && value) {
      magic_plan.forbid_unassigned_shifts = value;
      magic_plan.avoid_unassigned_shifts = false;
    } else if (key === "avoid_unassigned_shifts" && value) {
      magic_plan.avoid_unassigned_shifts = value;
      magic_plan.forbid_unassigned_shifts = false;
    } else if (key === "balance_shifts_by_employee") {
      Object.assign(magic_plan, withBalanceShiftsByEmployee(magic_plan, value));
    } else {
      magic_plan[key] = value;
    }
    this.setState({ magic_plan });
  };

  handleRestWindowBetweenBlocksTimeChange = (field, timeValue) => {
    const { magic_plan } = this.state;
    const current = normalizeRestWindowBetweenBlocks(magic_plan.rest_window_between_blocks);

    magic_plan.rest_window_between_blocks = {
      ...current,
      value: {
        ...current.value,
        [field]: timeValue,
      },
    };

    this.setState({ magic_plan });
  };

  handleChangeFractionShifts = (selectedValue) => {
    const { magic_plan, activeTabKey } = this.state;
    const value = typeof selectedValue === "string"
      ? selectedValue
      : selectedValue?.target?.value;
    if (value === "false") {
      magic_plan.fraction_shifts = false;
      magic_plan.fraction_shifts_minutes = null;
    } else if (value === "15" || value === "30" || value === "60") {
      magic_plan.fraction_shifts = true;
      magic_plan.fraction_shifts_minutes = parseInt(value, 10);
    }
    const nextState = { magic_plan };
    if (!magic_plan.fraction_shifts && activeTabKey === "fracciones") {
      nextState.activeTabKey = "config";
    }
    this.setState(nextState);
  };

  handleTimeLimitSelect = (value) => {
    const { magic_plan } = this.state;
    magic_plan.timelimit = value;
    this.setState({ magic_plan });
  };

  checkValidField = (name) => {
    return this.state.submitted && this.state.errors[name] !== undefined && this.state.errors[name] !== "";
  };

  checkInvalidRange = () => {
    const { magic_plan, submitted } = this.state;
    return submitted && (magic_plan.start === "" || magic_plan.end === "" || magic_plan.start === null || magic_plan.end === null);
  };

  // Validar si el período seleccionado corresponde a semanas completas (lunes a domingo)
  isCompleteWeeksPeriod = () => {
    const { magic_plan } = this.state;
    if (!magic_plan.start || !magic_plan.end) return false;

    const startDate = moment(magic_plan.start);
    const endDate = moment(magic_plan.end);

    // Verificar que la fecha de inicio sea lunes (día 1 en moment.js)
    const isStartMonday = startDate.day() === 1;

    // Verificar que la fecha de fin sea domingo (día 0 en moment.js)
    const isEndSunday = endDate.day() === 0;

    // Verificar que la diferencia en días sea múltiplo de 7 (semanas completas)
    const daysDifference = endDate.diff(startDate, 'days');
    const isCompleteWeeks = daysDifference >= 6 && (daysDifference + 1) % 7 === 0;

    return isStartMonday && isEndSunday && isCompleteWeeks;
  };

  // Validar si el período seleccionado corresponde a bloques de 4 semanas completas
  isCompleteFourWeeksPeriod = () => {
    const { magic_plan } = this.state;
    if (!magic_plan.start || !magic_plan.end) return false;

    const startDate = moment(magic_plan.start);
    const endDate = moment(magic_plan.end);

    const isStartMonday = startDate.day() === 1;
    const isEndSunday = endDate.day() === 0;

    const daysDifference = endDate.diff(startDate, 'days');
    const isCompleteFourWeeks = daysDifference >= 27 && (daysDifference + 1) % 28 === 0;

    return isStartMonday && isEndSunday && isCompleteFourWeeks;
  };

  prepareDayTypesToSubmit = () => this.state.selected_day_types.map((d) => d.name);
  prepareShiftsToSubmit = () => this.state.selected_shifts.map((s) => s.name);
  prepareHighLoadShiftsToSubmit = () => this.state.selected_high_load_shifts.map((s) => s.name);

  prepareShiftWeeklyLimitsToSubmit = () => this.state.shift_weekly_limits.map((shift) => ({
    id: shift.id,
    max_weekly_shift_assignments: parseInt(shift.max_weekly_shift_assignments, 10),
  }));

  formatHtmlErrorMessage = (message) => {
    if (!message || typeof message !== "string") return "";

    const htmlWithLineBreaks = message
      .replace(/<br\s*\/?>/gi, "\n")
      .replace(/<\/p>\s*<p>/gi, "\n")
      .replace(/<\/li>\s*<li>/gi, "\n");

    if (typeof document === "undefined") {
      return htmlWithLineBreaks
        .replace(/<[^>]*>/g, "")
        .replace(/\n{3,}/g, "\n\n")
        .trim();
    }

    const parser = document.createElement("div");
    parser.innerHTML = htmlWithLineBreaks;

    return (parser.textContent || parser.innerText || "")
      .replace(/\u00a0/g, " ")
      .replace(/\n{3,}/g, "\n\n")
      .trim();
  };

  handleSubmit = async (event) => {
    event.preventDefault();
    if (!this.state.configurationLoaded) return;

    const { magic_plan } = this.state;
    if (magic_plan.rest_window_between_blocks?.active) {
      const { start, end } = magic_plan.rest_window_between_blocks.value || {};
      if (!start || !end) {
        toastr.error(i18next.t('common.error'), i18next.t('magicPlan.errorRestWindowRequired'));
        return;
      }
      if (start >= end) {
        toastr.error(i18next.t('common.error'), i18next.t('magicPlan.errorRestWindowOrder'));
        return;
      }
    }

    this.setState({ isLoading: true });
    const { selected_day_types, selected_shifts, selected_high_load_shifts } = this.state;
    const props = this.props;
    const magic_plan_to_send = this.normalizeMagicPlanDecimalValues({ ...magic_plan });

    // Preparar rest_day_types
    if (selected_day_types.length > 0) {
      magic_plan_to_send.rest_day_types = { active: true, value: this.prepareDayTypesToSubmit() };
    } else {
      magic_plan_to_send.rest_day_types = { active: false, value: [] };
    }

    // Preparar next_week_rest_if_shifts_assigned
    if (selected_shifts.length > 0) {
      magic_plan_to_send.next_week_rest_if_shifts_assigned = { active: true, value: this.prepareShiftsToSubmit() };
    } else {
      magic_plan_to_send.next_week_rest_if_shifts_assigned = { active: false, value: [] };
    }

    // Preparar maximize_high_load_shift_priorities
    if (selected_high_load_shifts.length > 0 && magic_plan.maximize_shift_priorities) {
      magic_plan_to_send.maximize_high_load_shift_priorities = { active: true, value: this.prepareHighLoadShiftsToSubmit() };
    } else {
      magic_plan_to_send.maximize_high_load_shift_priorities = { active: false, value: [] };
    }

    magic_plan_to_send.concatenate_shifts_without_rests_per_day = magic_plan.fraction_shifts;
    magic_plan_to_send.balance_shifts_by_employee_high_priority =
      resolveBalanceShiftsHighPriority(magic_plan);

    // Convertir valores vacíos a null
    if (magic_plan.timelimit <= 0) magic_plan_to_send.timelimit = null;
    magic_plan_to_send.employees = this.getModifiedEmployeesPayload();
    magic_plan_to_send.rest_window_between_blocks = normalizeRestWindowBetweenBlocks(
      magic_plan.rest_window_between_blocks
    );
    magic_plan_to_send.shifts = this.prepareShiftWeeklyLimitsToSubmit();

    const response = await iPlanService.updateMagicPlan(magic_plan_to_send);
    if (response.ok) {
      this.setState({ isLoading: false, errors: {} }, () => {
        if (props.onPlanProcessing) props.onPlanProcessing(response, magic_plan_to_send.timelimit, magic_plan.iterative_calculation_in_days?.active);
        props.handleClose();
      });
    } else {
      // Procesar errores y mostrar toast
      const formattedErrors = errorFormating(response);
      const errorList = renderErrorList(response);

      // Extraer mensajes de error para el toast
      let errorMessage = i18next.t('magicPlan.errorPlanning');
      if (response.errors && response.errors.length > 0) {
        // Obtener las descripciones de los errores
        const descriptions = response.errors
          .map((err) => {
            const description = this.formatHtmlErrorMessage(err.description);
            const value = this.formatHtmlErrorMessage(err.value);
            return value ? `${description}: ${value}` : description;
          })
          .filter(desc => desc)
          .join("\n");
        if (descriptions) {
          errorMessage = descriptions;
        }
      }

      // Mostrar toast con los errores
      toastr.error(i18next.t('magicPlan.errorPlanningTitle'), errorMessage, {
        timeOut: 0,
        removeOnHover: false,
      });

      this.setState({
        isLoading: false,
        submitted: true,
        errors: formattedErrors,
        error_footer: errorList
      });
    }
  };

  // Toggles con modales de tiempo
  handleDifferentWeeklyShiftsToggle = () => {
    const { magic_plan } = this.state;
    const isActive = magic_plan.different_weekly_shifts.active;
    if (!isActive) {
      this.setState({ show_time_modal: true, active_modal_type: 'weekly', temp_time_config: { start: "", end: "" } });
    } else {
      magic_plan.different_weekly_shifts.active = false;
      magic_plan.different_weekly_shifts.start = "";
      magic_plan.different_weekly_shifts.end = "";
      this.setState({ magic_plan });
    }
  };
  handleDifferentWeeklyShiftsUntilFridayToggle = () => {
    const { magic_plan } = this.state;
    const isActive = magic_plan.different_weekly_shifts_until_friday.active;
    if (!isActive) {
      this.setState({ show_time_modal: true, active_modal_type: 'friday', temp_time_config_friday: { start: "", end: "" } });
    } else {
      magic_plan.different_weekly_shifts_until_friday.active = false;
      magic_plan.different_weekly_shifts_until_friday.start = "";
      magic_plan.different_weekly_shifts_until_friday.end = "";
      this.setState({ magic_plan });
    }
  };
  handleTimeConfigChange = (event) => {
    const { temp_time_config } = this.state;
    const key = event.target.id;
    const value = event.target.value;
    this.setState({ temp_time_config: { ...temp_time_config, [key]: value } });
  };
  handleTimeConfigFridayChange = (event) => {
    const { temp_time_config_friday } = this.state;
    const key = event.target.id;
    const value = event.target.value;
    this.setState({ temp_time_config_friday: { ...temp_time_config_friday, [key]: value } });
  };
  handleTimeModalAccept = () => {
    const { temp_time_config, magic_plan } = this.state;
    const { start, end } = temp_time_config;
    if (!start || !end) { toastr.error(i18next.t('common.error'), i18next.t('magicPlan.errorTimeRequired')); return; }
    if (start >= end) { toastr.error(i18next.t('common.error'), i18next.t('magicPlan.errorTimeOrder')); return; }
    const tz = moment.tz.guess();
    const base = moment().startOf('day');
    let mStart = base.clone().hour(+start.split(':')[0]).minute(+start.split(':')[1]);
    let mEnd = base.clone().hour(+end.split(':')[0]).minute(+end.split(':')[1]);
    if (mEnd.isBefore(mStart)) mEnd.add(1, 'day');
    const startUTC = mStart.tz(tz).utc().format("HH:mm[Z]");
    const endUTC = mEnd.tz(tz).utc().format("HH:mm[Z]");
    magic_plan.different_weekly_shifts.active = true;
    magic_plan.different_weekly_shifts.start = startUTC;
    magic_plan.different_weekly_shifts.end = endUTC;
    this.setState({ magic_plan, show_time_modal: false, temp_time_config: { start: "", end: "" } });
  };
  handleTimeModalFridayAccept = () => {
    const { temp_time_config_friday, magic_plan } = this.state;
    const { start, end } = temp_time_config_friday;
    if (!start || !end) { toastr.error(i18next.t('common.error'), i18next.t('magicPlan.errorTimeRequired')); return; }
    if (start >= end) { toastr.error(i18next.t('common.error'), i18next.t('magicPlan.errorTimeOrder')); return; }
    const tz = moment.tz.guess();
    const base = moment().startOf('day');
    let mStart = base.clone().hour(+start.split(':')[0]).minute(+start.split(':')[1]);
    let mEnd = base.clone().hour(+end.split(':')[0]).minute(+end.split(':')[1]);
    if (mEnd.isBefore(mStart)) mEnd.add(1, 'day');
    const startUTC = mStart.tz(tz).utc().format("HH:mm[Z]");
    const endUTC = mEnd.tz(tz).utc().format("HH:mm[Z]");
    magic_plan.different_weekly_shifts_until_friday.active = true;
    magic_plan.different_weekly_shifts_until_friday.start = startUTC;
    magic_plan.different_weekly_shifts_until_friday.end = endUTC;
    this.setState({ magic_plan, show_time_modal: false, temp_time_config_friday: { start: "", end: "" } });
  };
  handleTimeModalCancel = () => {
    this.setState({ show_time_modal: false, active_modal_type: null, temp_time_config: { start: "", end: "" } });
  };
  handleTimeModalFridayCancel = () => {
    this.setState({ show_time_modal: false, active_modal_type: null, temp_time_config_friday: { start: "", end: "" } });
  };

  getSelectedDayTypes = () =>
    this.state.selected_day_types.map((item) => (
      <li
        key={item.id}
        aria-label={item.description}
        title={item.description}
        className="tag-default tag-delete"
        style={{ backgroundColor: item.background_color && item.background_color }}
      >
        {item.name}
        <button type="button" onClick={() => this.removeDayType(item.id)} className="btn-tag-delete btn-transparent">
          <FiX />
        </button>
      </li>
    ));
  getSelectedShifts = () =>
    this.state.selected_shifts.map((item) => (
      <li key={item.id} aria-label={item.name} title={item.name} className="tag-default tag-delete">
        {item.name} {getFirstLastInterval(item.time_intervals, true)}
        <button type="button" onClick={() => this.removeShift(item.id)} className="btn-tag-delete btn-transparent">
          <FiX />
        </button>
      </li>
    ));
  removeDayType = (idDayType) => {
    this.setState((prev) => ({ selected_day_types: prev.selected_day_types.filter((d) => d.id !== parseInt(idDayType)) }));
  };
  addDayType = () => {
    let { selected_day_types, new_day_type } = this.state;
    let isValid = true;
    let day_type;
    const errors = {};
    if (new_day_type === undefined) {
      isValid = false;
    } else {
      day_type = this.props.select_day_types.find((item) => item.id === parseInt(new_day_type));
      if (day_type === undefined) { isValid = false; errors.day_type = false; }
    }
    if (isValid) {
      const exists = selected_day_types.find((item) => item.id === parseInt(new_day_type));
      if (!exists) selected_day_types = selected_day_types.concat(day_type);
      this.setState({ selected_day_types, errors: {}, new_day_type: {} });
    } else {
      this.setState({ errors });
    }
  };
  addBulkDayTypes = (start, end) => {
    const selected_day_types = [];
    const { select_day_types } = this.props;
    for (let i = start; i <= end; i++) {
      const day_type = select_day_types.find((item) => item.weekday === i);
      selected_day_types.push(day_type);
    }
    this.setState({ selected_day_types, errors: {}, new_day_type: {} });
  };
  handleAddChange = (event) => { this.setState({ [event.target.id]: event.target.value }); };
  removeShift = (idShift) => {
    this.setState((prev) => ({ selected_shifts: prev.selected_shifts.filter((s) => s.id !== parseInt(idShift)) }));
  };
  addShift = () => {
    let { selected_shifts, new_shift } = this.state;
    let isValid = true;
    let shift;
    const errors = {};
    if (new_shift === undefined) { isValid = false; }
    else {
      shift = this.props.attendance_shifts.find((item) => item.id === parseInt(new_shift));
      if (shift === undefined) { isValid = false; errors.shift = false; }
    }
    if (isValid) {
      const exists = selected_shifts.find((item) => item.id === parseInt(new_shift));
      if (!exists) selected_shifts = selected_shifts.concat(shift);
      this.setState({ selected_shifts, errors: {}, new_shift: {} });
    } else {
      this.setState({ errors });
    }
  };
  handleAddShiftChange = (event) => { this.setState({ [event.target.id]: event.target.value }); };

  getSelectedHighLoadShifts = () =>
    this.state.selected_high_load_shifts.map((item) => (
      <li key={item.id} aria-label={item.name} title={item.name} className="tag-default tag-delete">
        {item.name} {getFirstLastInterval(item.time_intervals, true)}
        <button type="button" onClick={() => this.removeHighLoadShift(item.id)} className="btn-tag-delete btn-transparent">
          <FiX />
        </button>
      </li>
    ));

  removeHighLoadShift = (idShift) => {
    this.setState((prev) => ({
      selected_high_load_shifts: prev.selected_high_load_shifts.filter((s) => s.id !== parseInt(idShift)),
    }));
  };

  addHighLoadShift = () => {
    let { selected_high_load_shifts, new_high_load_shift } = this.state;
    let isValid = true;
    let shift;
    const errors = {};
    if (new_high_load_shift === undefined) { isValid = false; }
    else {
      shift = this.props.attendance_shifts.find((item) => item.id === parseInt(new_high_load_shift));
      if (shift === undefined) { isValid = false; errors.shift = false; }
    }
    if (isValid) {
      const exists = selected_high_load_shifts.find((item) => item.id === parseInt(new_high_load_shift));
      if (!exists) selected_high_load_shifts = selected_high_load_shifts.concat(shift);
      const magic_plan = { ...this.state.magic_plan, maximize_shift_priorities: true };
      this.setState({ selected_high_load_shifts, magic_plan, errors: {}, new_high_load_shift: {} });
    } else {
      this.setState({ errors });
    }
  };

  handleAddHighLoadShiftChange = (event) => { this.setState({ [event.target.id]: event.target.value }); };

  handleShiftWeeklyLimitChange = (event) => {
    const { id, value } = event.target;
    this.setState((prev) => ({
      new_shift_weekly_limit: {
        ...prev.new_shift_weekly_limit,
        [id === "new_shift_weekly_limit_shift" ? "shift_id" : "max_weekly_shift_assignments"]: value,
      },
    }));
  };

  getSelectedShiftWeeklyLimits = () =>
    this.state.shift_weekly_limits.map((item) => (
      <li key={item.id} className="shift-weekly-limit-item tag-default tag-delete">
        <span className="shift-weekly-limit-item__name" title={item.name}>
          {item.name} {getFirstLastInterval(item.time_intervals, true)}
        </span>
        <FormGroup className="time-display time-display--compact shift-weekly-limit-item__input">
          <FormControl
            type="text"
            value={item.max_weekly_shift_assignments ?? ""}
            onChange={(event) => this.updateShiftWeeklyLimitValue(item.id, event.target.value)}
            placeholder="0"
          />
          <span className="time-display__unit">
            {i18next.t('magicPlan.unitTimes', { count: parseInt(item.max_weekly_shift_assignments, 10) || 0 })}
          </span>
        </FormGroup>
        <button
          type="button"
          onClick={() => this.removeShiftWeeklyLimit(item.id)}
          className="btn-tag-delete btn-transparent"
        >
          <FiX />
        </button>
      </li>
    ));

  removeShiftWeeklyLimit = (idShift) => {
    this.setState((prev) => ({
      shift_weekly_limits: prev.shift_weekly_limits.filter((shift) => shift.id !== parseInt(idShift, 10)),
    }));
  };

  updateShiftWeeklyLimitValue = (idShift, value) => {
    this.setState((prev) => ({
      shift_weekly_limits: prev.shift_weekly_limits.map((shift) => (
        shift.id === parseInt(idShift, 10)
          ? { ...shift, max_weekly_shift_assignments: value }
          : shift
      )),
    }));
  };

  addShiftWeeklyLimit = () => {
    const { shift_weekly_limits, new_shift_weekly_limit } = this.state;
    const shiftId = parseInt(new_shift_weekly_limit.shift_id, 10);
    const maxWeeklyAssignments = parseInt(new_shift_weekly_limit.max_weekly_shift_assignments, 10);
    const errors = {};

    if (!shiftId) {
      errors.shift_weekly_limit = false;
      this.setState({ errors });
      return;
    }

    if (!maxWeeklyAssignments || maxWeeklyAssignments <= 0) {
      errors.shift_weekly_limit_value = false;
      this.setState({ errors });
      return;
    }

    const shift = this.props.attendance_shifts.find((item) => item.id === shiftId);
    if (!shift) {
      errors.shift_weekly_limit = false;
      this.setState({ errors });
      return;
    }

    const exists = shift_weekly_limits.find((item) => item.id === shiftId);
    if (exists) {
      this.setState({ errors: { shift_weekly_limit: false } });
      return;
    }

    this.setState({
      shift_weekly_limits: shift_weekly_limits.concat({
        ...shift,
        max_weekly_shift_assignments: maxWeeklyAssignments,
      }),
      errors: {},
      new_shift_weekly_limit: { shift_id: "", max_weekly_shift_assignments: "" },
    });
  };

  toNumber = (value) => {
    if (value === null || value === undefined || value === "") return null;
    const normalized = typeof value === "string"
      ? value.includes(",") ? value.replace(/\./g, "").replace(",", ".") : value
      : value;
    const parsed = Number(normalized);
    return Number.isNaN(parsed) ? null : parsed;
  };

  isEmptyTargetHoursOverride = (value) => (
    value === null || value === undefined || value === ""
  );

  normalizeTargetHoursEmployees = (employees) => {
    if (!Array.isArray(employees)) return [];

    return employees
      .map((employee) => {
        if (!employee) return null;

        if (!this.isEmptyTargetHoursOverride(employee.custom_target_hours)) {
          return employee;
        }

        const { custom_target_hours, ...rest } = employee;
        const hasOtherFields = Object.keys(rest).some((key) => key !== "id");
        return hasOtherFields ? rest : null;
      })
      .filter(Boolean);
  };

  hasPendingEmptyTargetHoursOverrides = () => {
    const employees = this.state.magic_plan?.employees;
    if (!Array.isArray(employees)) return false;
    return employees.some((employee) => employee?.custom_target_hours === "");
  };

  commitClearedTargetHoursOverrides = (callback) => {
    if (!this.hasPendingEmptyTargetHoursOverrides()) {
      if (callback) callback();
      return;
    }

    const { magic_plan } = this.state;
    const employees = this.normalizeTargetHoursEmployees(magic_plan.employees);

    this.setState({
      magic_plan: {
        ...magic_plan,
        employees,
      },
    }, () => {
      if (callback) callback();
    });
  };

  getEmployeeTargetHours = (employee) => {
    const rawCustom = employee?.custom_target_hours;
    const hasCustomOverride = rawCustom !== null
      && rawCustom !== undefined
      && rawCustom !== "";
    return this.toNumber(
      hasCustomOverride
        ? rawCustom
        : (employee?.target_hours ?? employee?.target_hours_per_week ?? 0)
    ) || 0;
  };

  getEmployeeTargetHoursDisplay = (employee) => {
    const rawCustom = employee?.custom_target_hours;
    // Allow empty input while editing
    if (rawCustom === "") return "";

    const hasCustomOverride = rawCustom !== null
      && rawCustom !== undefined;
    if (hasCustomOverride) {
      return this.formatHours(this.toNumber(rawCustom) || 0, 2, 2);
    }

    const baseValue = this.toNumber(
      employee?.target_hours ?? employee?.target_hours_per_week ?? 0
    ) || 0;
    const isBlockMode = this.state.magic_plan?.iterative_calculation_in_days?.active;
    const displayValue = isBlockMode
      ? baseValue / this.getPlanningBlocksCount()
      : baseValue;
    return this.formatHours(displayValue, 2, 2);
  };

  hasManualTargetHoursOverrides = () => {
    if (!this.state.assignmentsLoaded) return false;

    const planningEmployees = Array.isArray(this.state.magic_plan?.employees)
      ? this.state.magic_plan.employees
      : [];
    if (planningEmployees.length === 0) return false;

    const objectiveAssignmentsByEmployee = this.buildAssignmentIndex(
      this.state.objectiveAssignments || []
    );

    return planningEmployees.some((employee) => {
      const employeeId = this.normalizeEmployeeId(employee?.id);
      if (!employeeId) return false;

      const rawCustom = employee?.custom_target_hours;
      const hasCustomOverride = rawCustom !== null
        && rawCustom !== undefined
        && rawCustom !== "";
      if (!hasCustomOverride) return false;

      const accountObjective = this.roundToTwoDecimals(
        objectiveAssignmentsByEmployee[employeeId]?.value ?? 0
      ) ?? 0;
      const customHours = this.roundToTwoDecimals(rawCustom);
      if (customHours === null) return false;

      return customHours !== accountObjective;
    });
  };

  getEmployeeBalanceHours = (employee) => (
    this.toNumber(
      employee?.balance_until_today
      ?? employee?.custom_balance_until_today
      ?? employee?.balance_hours
      ?? employee?.hours_balance
      ?? employee?.balance
      ?? employee?.current_balance
      ?? 0
    ) || 0
  );

  getEmployeeName = (employee) => (
    `${employee?.name || ""} ${employee?.surname || ""}`.trim() || employee?.full_name || "Sin nombre"
  );

  getEmployeeInitials = (employee) => {
    const parts = this.getEmployeeName(employee).split(" ").filter(Boolean);
    return parts.slice(0, 2).map((part) => part[0]?.toUpperCase() || "").join("") || "--";
  };

  handleTargetHoursSort = (key) => {
    this.setState((prevState) => {
      const { targetHoursSort } = prevState;
      const nextDirection =
        targetHoursSort.key === key && targetHoursSort.direction === "asc"
          ? "desc"
          : "asc";

      return {
        targetHoursSort: {
          key,
          direction: nextDirection,
        },
      };
    });
  };

  getTargetHoursSortIndicator = (key) => {
    const { targetHoursSort } = this.state;
    if (targetHoursSort.key !== key) return null;
    return (
      <span
        style={{
          fontSize: "10px",
          color: "#9aa0a6",
          marginLeft: "4px",
          verticalAlign: "middle",
        }}
      >
        {targetHoursSort.direction === "asc" ? "▲" : "▼"}
      </span>
    );
  };

  formatHours = (value, minimumFractionDigits = 0, maximumFractionDigits = 2) => {
    const parsed = this.roundToTwoDecimals(value) ?? 0;
    return formatNumber(parsed);
  };

  formatSignedHours = (value) => {
    const parsed = this.toNumber(value) || 0;
    const sign = parsed >= 0 ? "+" : "-";
    return `${sign}${this.formatHours(Math.abs(parsed), 2, 2)}h`;
  };

  roundToTwoDecimals = (value) => {
    const parsed = this.toNumber(value);
    if (parsed === null) return null;
    return Number(parsed.toFixed(2));
  };

  getPlanningWeeksCount = () => {
    const start = this.state.magic_plan?.start || this.props.start;
    const end = this.state.magic_plan?.end || this.props.end;
    if (!start || !end) return 1;
    const days = moment(end, "YYYY-MM-DD").diff(moment(start, "YYYY-MM-DD"), "days") + 1;
    return Math.max(Math.round(days / 7), 1);
  };

  // Retorna el número de bloques de planificación según la opción seleccionada
  getPlanningBlocksCount = () => {
    const blockValue = this.state.magic_plan?.iterative_calculation_in_days?.value;
    const weeksCount = this.getPlanningWeeksCount();
    if (blockValue === 27) {
      return Math.max(Math.round(weeksCount / 4), 1);
    }
    return weeksCount;
  };

  normalizeEmployeeId = (value) => {
    const parsed = parseInt(value, 10);
    return Number.isNaN(parsed) ? null : parsed;
  };

  getUniqueEmployeeIds = (employees = []) => (
    [...new Set(
      employees
        .map((employee) => this.normalizeEmployeeId(employee?.id))
        .filter((employeeId) => !!employeeId)
    )]
  );

  getUniquePlanAssignmentEmployeeIds = (planAssignments = []) => {
    const uniqueIds = [];
    const seen = new Set();

    planAssignments.forEach((assignment) => {
      const employeeId = this.normalizeEmployeeId(assignment?.employee?.id);
      if (employeeId && !seen.has(employeeId)) {
        seen.add(employeeId);
        uniqueIds.push(employeeId);
      }
    });

    return uniqueIds;
  };

  getEmployeeNameById = (employeeId, pools = []) => {
    for (const pool of pools) {
      if (!Array.isArray(pool)) continue;

      const match = pool.find(
        (employee) => this.normalizeEmployeeId(employee?.id) === employeeId
      );

      if (match) {
        const fullName = `${match.name || ""} ${match.surname || ""}`.trim();
        if (fullName) return fullName;
      }
    }

    return `ID ${employeeId}`;
  };

  logTargetHoursDiagnostics = () => {
    const { magic_plan, objectiveAssignments, balanceAssignments } = this.state;
    const { planAssignments = [] } = this.props;
    const groupId = this.normalizeEmployeeId(magic_plan?.group_id);
    const selectedGroup = this.getSelectedGroup();
    const groupEmployees = Array.isArray(selectedGroup?.employees) ? selectedGroup.employees : [];
    const planningEmployees = Array.isArray(magic_plan?.employees) ? magic_plan.employees : [];
    const objectiveAssignmentsByEmployee = this.buildAssignmentIndex(objectiveAssignments || []);
    const balanceAssignmentsByEmployee = this.buildAssignmentIndex(balanceAssignments || []);

    const planEmployeeIds = this.getUniquePlanAssignmentEmployeeIds(planAssignments);
    const groupEmployeeIds = this.getUniqueEmployeeIds(groupEmployees);
    const planningEmployeeIds = this.getUniqueEmployeeIds(planningEmployees);
    const objectiveEmployeeIds = Object.keys(objectiveAssignmentsByEmployee)
      .map((id) => this.normalizeEmployeeId(id))
      .filter((employeeId) => !!employeeId);
    const balanceEmployeeIds = Object.keys(balanceAssignmentsByEmployee)
      .map((id) => this.normalizeEmployeeId(id))
      .filter((employeeId) => !!employeeId);

    const mergedEmployeeIds = [...new Set([
      ...groupEmployeeIds,
      ...planningEmployeeIds,
      ...objectiveEmployeeIds,
      ...balanceEmployeeIds,
    ])];

    const planEmployeeIdSet = new Set(planEmployeeIds);
    const mergedEmployeeIdSet = new Set(mergedEmployeeIds);
    const extraInIa = mergedEmployeeIds.filter((employeeId) => !planEmployeeIdSet.has(employeeId));
    const missingInIa = planEmployeeIds.filter((employeeId) => !mergedEmployeeIdSet.has(employeeId));

    const signature = JSON.stringify({
      groupId,
      start: magic_plan?.start || "",
      end: magic_plan?.end || "",
      planEmployeeIds,
      groupEmployeeIds,
      planningEmployeeIds,
      objectiveEmployeeIds,
      balanceEmployeeIds,
      mergedEmployeeIds,
      extraInIa,
      missingInIa,
    });

    if (signature === this.targetHoursDebugSignature) return;
    this.targetHoursDebugSignature = signature;

    const objectiveEmployees = (objectiveAssignments || [])
      .map((assignment) => assignment?.employee)
      .filter(Boolean);
    const balanceEmployees = (balanceAssignments || [])
      .map((assignment) => assignment?.employee)
      .filter(Boolean);
    const planEmployees = (planAssignments || [])
      .map((assignment) => assignment?.employee)
      .filter(Boolean);
    const namePools = [
      planEmployees,
      planningEmployees,
      groupEmployees,
      objectiveEmployees,
      balanceEmployees,
    ];

    const formatWithName = (ids) =>
      ids.map((employeeId) => `${employeeId} - ${this.getEmployeeNameById(employeeId, namePools)}`);

    console.groupCollapsed(
      `[PlanMagic][Horas objetivo] Diagnóstico empleados g:${groupId || "-"} ${magic_plan?.start || "-"}..${magic_plan?.end || "-"}`
    );
    console.log("Plan (orden visible):", formatWithName(planEmployeeIds));
    console.log("Grupo (detailed_groups):", formatWithName(groupEmployeeIds));
    console.log("Configuración IA (magic_plan.employees):", formatWithName(planningEmployeeIds));
    console.log("Cuenta horas objetivo:", formatWithName(objectiveEmployeeIds));
    console.log("Cuenta balance:", formatWithName(balanceEmployeeIds));
    console.log("Merge final IA:", formatWithName(mergedEmployeeIds));
    console.log("Extra en IA vs plan:", formatWithName(extraInIa));
    console.log("Faltantes en IA vs plan:", formatWithName(missingInIa));
    console.groupEnd();
  };

  getObjectiveKpis = (assigmentsData) => {
    const hasAssigmentsData = Array.isArray(assigmentsData) && assigmentsData.length > 0;
    const stats = hasAssigmentsData ? calcAssigmentsStats(assigmentsData) : {};
    const targetHours = this.toNumber(stats.targetHours) || 0;
    const neededHours = this.toNumber(stats.neededHours) || 0;

    return {
      targetHours,
      neededHours,
      differenceHours: targetHours - neededHours,
    };
  };

  getObjectiveKpisFromEmployees = (employees, assigmentsData) => {
    const baseKpis = this.getObjectiveKpis(assigmentsData);
    const employeeList = Array.isArray(employees) ? employees : [];

    if (employeeList.length === 0) {
      return baseKpis;
    }

    const targetHours = employeeList.reduce(
      (acc, employee) => acc + this.getEmployeeTargetHours(employee),
      0
    );
    const neededHours = baseKpis.neededHours;

    return {
      targetHours,
      neededHours,
      differenceHours: targetHours - neededHours,
    };
  };

  getAssignmentRows = (response) => {
    if (Array.isArray(response?.data)) return response.data;
    if (Array.isArray(response)) return response;
    return [];
  };

  getAssignmentCounterValue = (assignment) => (
    (assignment?.counters || []).reduce((acc, counter) => {
      const value = this.toNumber(counter?.value) || 0;
      return acc + value;
    }, 0)
  );

  buildAssignmentIndex = (assignments) => (
    assignments.reduce((acc, assignment) => {
      const employeeId = this.normalizeEmployeeId(
        assignment?.employee?.id || assignment?.employee_id
      );
      if (!employeeId) return acc;

      const currentValue = this.getAssignmentCounterValue(assignment);
      const previous = acc[employeeId] || { value: 0, employee: null };

      acc[employeeId] = {
        value: previous.value + currentValue,
        employee: assignment?.employee || previous.employee,
      };
      return acc;
    }, {})
  );

  getTargetHoursEmployeeIds = () => {
    const planEmployeeIds = this.getUniquePlanAssignmentEmployeeIds(this.props.planAssignments || []);
    if (planEmployeeIds.length > 0) return planEmployeeIds;

    const selectedGroup = this.getSelectedGroup();
    const groupEmployees = Array.isArray(selectedGroup?.employees) ? selectedGroup.employees : [];
    const planningEmployees = Array.isArray(this.state.magic_plan?.employees) ? this.state.magic_plan.employees : [];
    const objectiveAssignmentsByEmployee = this.buildAssignmentIndex(this.state.objectiveAssignments || []);
    const balanceAssignmentsByEmployee = this.buildAssignmentIndex(this.state.balanceAssignments || []);

    return [...new Set([
      ...groupEmployees
        .map((employee) => this.normalizeEmployeeId(employee?.id))
        .filter((employeeId) => !!employeeId),
      ...planningEmployees
        .map((employee) => this.normalizeEmployeeId(employee?.id))
        .filter((employeeId) => !!employeeId),
      ...Object.keys(objectiveAssignmentsByEmployee)
        .map((id) => this.normalizeEmployeeId(id))
        .filter((employeeId) => !!employeeId),
      ...Object.keys(balanceAssignmentsByEmployee)
        .map((id) => this.normalizeEmployeeId(id))
        .filter((employeeId) => !!employeeId),
    ])];
  };

  preloadEmployeeAvatars = async () => {
    if (this.state.activeTabKey !== "horas-objetivo") return;

    const employeeIds = this.getTargetHoursEmployeeIds();
    const signature = employeeIds.join(",");

    if (signature === this.state.avatarEmployeesSignature) return;

    this.setState({ avatarEmployeesSignature: signature });

    const missingIds = employeeIds.filter((employeeId) => this.state.employeeAvatars[employeeId] === undefined);
    if (missingIds.length === 0) return;

    const cachedEntries = await Promise.all(
      missingIds.map(async (employeeId) => {
        const cached = await getCachedAvatar(employeeId);
        return [employeeId, cached];
      })
    );

    const cachedHits = cachedEntries.filter(([, avatar]) => avatar !== null && avatar !== undefined);
    const uncachedIds = cachedEntries
      .filter(([, avatar]) => avatar === null || avatar === undefined)
      .map(([employeeId]) => employeeId);

    if (cachedHits.length > 0) {
      this.setState((prevState) => ({
        employeeAvatars: cachedHits.reduce((acc, [employeeId, avatar]) => {
          acc[employeeId] = avatar || null;
          return acc;
        }, { ...prevState.employeeAvatars }),
      }));
    }

    if (uncachedIds.length === 0) return;

    const avatarEntries = await Promise.all(
      uncachedIds.map(async (employeeId) => {
        try {
          const avatar = await employeesService.getAvatar(employeeId);
          const value = avatar || "";
          await saveCachedAvatar(employeeId, value);
          return [employeeId, value || null];
        } catch (error) {
          return [employeeId, null];
        }
      })
    );

    this.setState((prevState) => ({
      employeeAvatars: avatarEntries.reduce((acc, [employeeId, avatar]) => {
        acc[employeeId] = avatar;
        return acc;
      }, { ...prevState.employeeAvatars }),
    }));
  };

  getModifiedEmployeesPayload = () => {
    const planningEmployees = Array.isArray(this.state.magic_plan?.employees)
      ? this.state.magic_plan.employees
      : [];
    const objectiveAssignmentsByEmployee = this.buildAssignmentIndex(this.state.objectiveAssignments || []);

    return planningEmployees
      .filter((employee) => !!employee?.id)
      .map((employee) => {
        const employeeId = this.normalizeEmployeeId(employee.id);
        if (!employeeId) return null;
        const customTargetHours = this.roundToTwoDecimals(employee.custom_target_hours);
        const originalTargetHours = this.roundToTwoDecimals(
          objectiveAssignmentsByEmployee[employeeId]?.value ?? 0
        );
        const hasChanged = customTargetHours !== null && customTargetHours !== originalTargetHours;

        if (!hasChanged) return null;

        return {
          id: employeeId,
          custom_target_hours: customTargetHours,
        };
      })
      .filter(Boolean);
  };

  loadAccountAssignmentsData = async () => {
    const groupId = parseInt(this.state.magic_plan?.group_id || this.props.group_id, 10);
    const planningStart = this.state.magic_plan?.start || this.props.start;
    const planningEnd = this.state.magic_plan?.end || this.props.end;

    if (!groupId || !planningStart || !planningEnd) {
      this.setState({
        objectiveAssignments: [],
        balanceAssignments: [],
        assignmentsLoading: false,
        assignmentsLoaded: true,
      });
      return;
    }

    const requestToken = Date.now();
    this.assignmentsRequestToken = requestToken;

    this.setState({ assignmentsLoading: true });

    try {
      const planningStartDate = moment(planningStart, "YYYY-MM-DD");
      const balanceStartDate = "2020-01-01";
      const dayBeforePlanning = planningStartDate.isValid()
        ? planningStartDate.clone().subtract(1, "day").format("YYYY-MM-DD")
        : planningStart;
      const balanceEndDate = dayBeforePlanning;
      const shouldFetchBalance = moment(balanceEndDate, "YYYY-MM-DD").isSameOrAfter(moment(balanceStartDate, "YYYY-MM-DD"), "day");

      const objectivePromise = accountsService.getAccountAssignments({
        group_id: groupId,
        start: planningStart,
        end: planningEnd,
        account_system_id: 4,
      });

      const balancePromise = shouldFetchBalance
        ? accountsService.getAccountAssignments({
          group_id: groupId,
          start: balanceStartDate,
          end: balanceEndDate,
          transaction_rule_system_id: 5,
        })
        : Promise.resolve([]);

      const [objectiveResponse, balanceResponse] = await Promise.all([
        objectivePromise,
        balancePromise,
      ]);

      if (this.assignmentsRequestToken !== requestToken) return;

      this.setState({
        objectiveAssignments: this.getAssignmentRows(objectiveResponse),
        balanceAssignments: this.getAssignmentRows(balanceResponse),
        assignmentsLoading: false,
        assignmentsLoaded: true,
      });
    } catch (error) {
      if (this.assignmentsRequestToken !== requestToken) return;

      this.targetHoursDataKey = null;
      this.setState({
        objectiveAssignments: [],
        balanceAssignments: [],
        assignmentsLoading: false,
        assignmentsLoaded: false,
      });
    }
  };

  handleEmployeeTargetHoursChange = (employeeId, value) => {
    const { magic_plan } = this.state;
    const employees = Array.isArray(magic_plan.employees) ? [...magic_plan.employees] : [];
    const rawValue = String(value || "").replace(/[^\d.,]/g, "");
    let normalizedValue = rawValue;

    if (normalizedValue.includes(",")) {
      normalizedValue = normalizedValue.replace(/\./g, "").replace(",", ".");
    } else {
      const dotParts = normalizedValue.split(".");
      if (dotParts.length > 2) {
        normalizedValue = `${dotParts[0]}.${dotParts.slice(1).join("")}`;
      }
    }

    const [integerPart = "", decimalPart = ""] = normalizedValue.split(".");
    const cleanIntegerPart = integerPart.replace(/[^\d]/g, "");
    const cleanDecimalPart = decimalPart.replace(/[^\d]/g, "").slice(0, 2);
    const sanitizedValue = normalizedValue.includes(".")
      ? `${cleanIntegerPart}.${cleanDecimalPart}`
      : cleanIntegerPart;
    const parsedValue = sanitizedValue === "" ? "" : (this.roundToTwoDecimals(sanitizedValue) ?? 0);
    const normalizedEmployeeId = this.normalizeEmployeeId(employeeId);
    const employeeIndex = employees.findIndex(
      (employee) => this.normalizeEmployeeId(employee?.id) === normalizedEmployeeId
    );

    if (employeeIndex > -1) {
      employees[employeeIndex] = {
        ...employees[employeeIndex],
        custom_target_hours: parsedValue,
      };
    } else {
      employees.push({
        id: normalizedEmployeeId || employeeId,
        custom_target_hours: parsedValue,
      });
    }

    this.setState({
      magic_plan: {
        ...magic_plan,
        employees,
      },
    });
  };

  getNavTitle = () => {
    const { activeTabKey, magic_plan } = this.state;
    if (activeTabKey === "config") return i18next.t("magicPlan.sectionConfig");
    const option = this.getRestrictionTabOptions(magic_plan).find(
      (item) => item.value === activeTabKey
    );
    return option?.label || i18next.t("magicPlan.sectionRestrictions");
  };

  getSelectedTimelimit = () => {
    const current = Number(this.state.magic_plan.timelimit) || RECOMMENDED_TIMELIMIT;
    const match = TIME_LIMIT_OPTIONS.find((option) => option.value === current);
    return match ? match.value : RECOMMENDED_TIMELIMIT;
  };

  renderHint = (content) => (
    <div className="plan-magic-hint">
      <FiInfo className="plan-magic-hint__icon" size={16} />
      <span>{content}</span>
    </div>
  );

  renderInfoTooltip = (text) => (
    <OverlayTrigger
      placement="top"
      overlay={<Tooltip className="plan-magic-tooltip">{text}</Tooltip>}
    >
      <button type="button" className="plan-magic-info" aria-label={text}>
        <FiInfo size={16} />
      </button>
    </OverlayTrigger>
  );

  renderShiftModeSelector = (fractionValue) => {
    const atLeastFlexible = fractionValue === FRACTION_SHIFT_OPTIONS[0].value;
    const atMostFlexible =
      fractionValue === FRACTION_SHIFT_OPTIONS[FRACTION_SHIFT_OPTIONS.length - 1].value;

    return (
      <div className="plan-magic-shift-mode">
        <div
          className="plan-magic-shift-mode__options"
          role="group"
          aria-label={i18next.t("magicPlan.labelDetailLevel")}
        >
          {FRACTION_SHIFT_OPTIONS.map((option) => {
            const isActive = fractionValue === option.value;

            return (
              <button
                key={option.value}
                type="button"
                className={`plan-magic-shift-mode__card ${isActive ? "is-active" : ""}`}
                aria-pressed={isActive}
                onClick={() => this.handleChangeFractionShifts(option.value)}
              >
                <span className="plan-magic-shift-mode__title">
                  {i18next.t(option.labelKey)}
                </span>
                <span className="plan-magic-shift-mode__meter" aria-hidden="true">
                  {option.segments.map((filled, index) => (
                    <span
                      key={index}
                      className={`plan-magic-shift-mode__segment ${filled ? "is-filled" : ""}`}
                    />
                  ))}
                </span>
                <span className="plan-magic-shift-mode__caption">
                  {i18next.t(option.captionKey)}
                </span>
              </button>
            );
          })}
        </div>
        <div className="plan-magic-shift-mode__scale">
          <button
            type="button"
            aria-disabled={atLeastFlexible}
            onClick={() => {
              if (atLeastFlexible) return;
              this.handleChangeFractionShifts(stepFractionShiftValue(fractionValue, -1));
            }}
          >
            <span aria-hidden="true">←</span>
            {i18next.t("magicPlan.shiftLessFlexible")}
          </button>
          <button
            type="button"
            aria-disabled={atMostFlexible}
            onClick={() => {
              if (atMostFlexible) return;
              this.handleChangeFractionShifts(stepFractionShiftValue(fractionValue, 1));
            }}
          >
            {i18next.t("magicPlan.shiftMoreEffort")}
            <span aria-hidden="true">→</span>
          </button>
        </div>
      </div>
    );
  };

  renderNav = () => {
    const { magic_plan, activeTabKey } = this.state;
    const restrictionOptions = this.getRestrictionTabOptions(magic_plan);

    return (
      <div className="plan-magic-nav">
        <h5 className="modal-title plan-magic-nav__title">{i18next.t("magicPlan.title")}</h5>
        <nav className="plan-magic-nav__list" aria-label={i18next.t("magicPlan.title")}>
          <button
            type="button"
            className={`plan-magic-nav__item ${activeTabKey === "config" ? "is-active" : ""}`}
            onClick={() => this.handleTabSelect("config")}
          >
            {i18next.t("magicPlan.sectionConfig")}
          </button>
          <p className="plan-magic-nav__group">{i18next.t("magicPlan.navRestrictions")}</p>
          {restrictionOptions.map((option) => (
            <button
              key={option.value}
              type="button"
              className={`plan-magic-nav__item ${activeTabKey === option.value ? "is-active" : ""}`}
              onClick={() => this.handleTabSelect(option.value)}
            >
              {option.label}
            </button>
          ))}
        </nav>
      </div>
    );
  };

  renderConfigPanel = () => {
    const { magic_plan } = this.state;
    const selectedGroup = this.getSelectedGroup();
    const groupName = selectedGroup
      ? `${selectedGroup.name}${selectedGroup.is_multigroup ? ` (${i18next.t("magicPlan.multigroup")})` : ""}`
      : "—";
    const periodLabel = magic_plan.start && magic_plan.end
      ? `${moment(magic_plan.start).format("DD/MM/YYYY")} → ${moment(magic_plan.end).format("DD/MM/YYYY")}`
      : "—";
    const fractionValue = magic_plan.fraction_shifts
      ? String(magic_plan.fraction_shifts_minutes || "false")
      : "false";
    const iterativeValue = magic_plan.iterative_calculation_in_days.active
      ? String(magic_plan.iterative_calculation_in_days.value)
      : "0";
    const selectedTimelimit = this.getSelectedTimelimit();

    return (
      <div className="plan-magic-config">
        <section className="plan-magic-config-card">
          <div className="plan-magic-config__meta">
            <div>
              <span className="plan-magic-config__meta-label">{i18next.t("magicPlan.labelGroup")}</span>
              <span className="plan-magic-config__meta-value">{groupName}</span>
            </div>
            <div className="plan-magic-config__meta-period">
              <span className="plan-magic-config__meta-label">{i18next.t("magicPlan.labelPeriod")}</span>
              <span className="plan-magic-config__meta-value">{periodLabel}</span>
            </div>
          </div>
        </section>

        <section className="plan-magic-config-card">
          <div className="plan-magic-config-card__header">
            <span className="plan-magic-config-card__index">1</span>
            <div>
              <h3>{i18next.t("magicPlan.labelDetailLevel")}</h3>
              <p>{i18next.t("magicPlan.hintDetailLevel")}</p>
            </div>
          </div>
          {this.renderShiftModeSelector(fractionValue)}
          {magic_plan.fraction_shifts
            ? this.renderHint(
                <>
                  {i18next.t("magicPlan.hintFractionsNeedsPre")}
                  <button
                    type="button"
                    className="plan-magic-hint__link"
                    onClick={() => this.handleTabSelect("fracciones")}
                  >
                    {i18next.t("magicPlan.hintFractionsNeedsLink")}
                  </button>
                  {i18next.t("magicPlan.hintFractionsNeedsPost")}
                </>
              )
            : null}
        </section>

        <section className="plan-magic-config-card">
          <div className="plan-magic-config-card__header">
            <span className="plan-magic-config-card__index">2</span>
            <div>
              <div className="plan-magic-config-card__title">
                <h3>{i18next.t("magicPlan.labelTimeLimit")}</h3>
                {this.renderInfoTooltip(i18next.t("magicPlan.hintTimeLimitHelp"))}
              </div>
              <p>{i18next.t("magicPlan.hintTimeLimit")}</p>
            </div>
          </div>
          <div className="plan-magic-time-track">
            <div className="plan-magic-time-track__line" />
            {TIME_LIMIT_OPTIONS.map((option) => (
              <button
                key={option.value}
                type="button"
                className={`plan-magic-time-track__step ${selectedTimelimit === option.value ? "is-active" : ""} ${option.recommended ? "is-recommended" : ""}`}
                onClick={() => this.handleTimeLimitSelect(option.value)}
              >
                {option.recommended && (
                  <span className="plan-magic-time-track__recommended">
                    {i18next.t("magicPlan.recommended")}
                  </span>
                )}
                <span className="plan-magic-time-track__dot" />
                <span className="plan-magic-time-track__label">{i18next.t(option.labelKey)}</span>
              </button>
            ))}
          </div>
        </section>

        <section className="plan-magic-config-card">
          <div className="plan-magic-config-card__header">
            <span className="plan-magic-config-card__index">3</span>
            <div>
              <div className="plan-magic-config-card__title">
                <h3>{i18next.t("magicPlan.labelIterativeBlocks")}</h3>
                {this.renderInfoTooltip(i18next.t("magicPlan.hintIterativeBlocksHelp"))}
              </div>
              <p>{i18next.t("magicPlan.hintIterativeBlocks")}</p>
            </div>
          </div>
          <PillToggleGroup
            className="plan-magic-config-pills"
            ariaLabel={i18next.t("magicPlan.labelIterativeBlocks")}
            options={[
              { value: "0", label: i18next.t("magicPlan.iterativeBlockFullShort") },
              { value: "6", label: i18next.t("magicPlan.iterativeBlockWeekly") },
              { value: "27", label: i18next.t("magicPlan.iterativeBlockFourWeeks") },
            ]}
            value={iterativeValue}
            onChange={this.handleIterativeBlockChange}
          />
        </section>
      </div>
    );
  };

  renderFooter = () => (
    <div className="plan-magic-footer">
      <button type="button" onClick={this.props.handleClose} className="btn btn-outline-primary">
        {i18next.t("common.cancel")}
      </button>
      <SubmitButton
        type="submit"
        isLoading={this.state.isLoading}
        disabled={!this.state.configurationLoaded}
        text={i18next.t("magicPlan.btnSchedule")}
        className="btn-wand"
        loadingText={i18next.t("magicPlan.btnScheduling")}
        iconBefore={<FaMagic className="icon-wand mr-2" />}
      />
    </div>
  );

  renderRestrictionValueRow = ({ fieldKey, label, unit, active, unitCount }) => {
    const field = this.state.magic_plan[fieldKey] || {};
    const inputKey = `${fieldKey}.value`;
    const unitLabel =
      unitCount !== undefined
        ? i18next.t(unit, { count: unitCount })
        : i18next.t(unit);

    return (
      <div className="switch-with-input">
        <FormGroup
          className="form-group form-group-flex form-row-switch form-group-main"
          controlId={`${fieldKey}.active`}
        >
          <FormLabel>{label}</FormLabel>
          {active ? (
            <FormGroup className="time-display time-display--compact" controlId={inputKey}>
              <FormControl
                type="text"
                value={field.value || ""}
                ref={this.inputRefs[inputKey]}
                onChange={this.handleChange}
                placeholder="0"
                isInvalid={this.checkValidField(inputKey)}
              />
              <span className="time-display__unit">{unitLabel}</span>
            </FormGroup>
          ) : null}
          <Switch
            controlId={`${fieldKey}.active`}
            isOn={active}
            handleToggle={this.handleChange}
          />
        </FormGroup>
      </div>
    );
  };

  renderRestrictionBlock = (titleKey, children) => (
    <div className="restriction-block">
      <div className="restriction-block__title">{i18next.t(titleKey)}</div>
      {children}
    </div>
  );

  renderRight = () => {
    const { magic_plan, objectiveAssignments, balanceAssignments, assignmentsLoading, employeeAvatars } = this.state;
    const { attendance_shifts, select_day_types, assigmentsData, planAssignments = [] } = this.props;
    const different_weekly_shifts = magic_plan.different_weekly_shifts.active;
    const different_weekly_shifts_until_friday = magic_plan.different_weekly_shifts_until_friday.active;
    const optionShiftList = attendance_shifts.length > 0 && attendance_shifts.map((item, i) => (
      <option key={i} value={item.id}>{item.name} {getFirstLastInterval(item.time_intervals, true)}</option>
    ));
    const { shift_weekly_limits } = this.state;
    const optionShiftWeeklyLimitList = attendance_shifts.length > 0 && attendance_shifts
      .filter((item) => !shift_weekly_limits.some((limit) => limit.id === item.id))
      .map((item, i) => (
        <option key={i} value={item.id}>{item.name} {getFirstLastInterval(item.time_intervals, true)}</option>
      ));
    const optionDayTypeList = select_day_types.length > 0 && select_day_types.map((item, i) => (
      <option key={i} value={item.id}>{item.description ? item.name + " (" + item.description + ")" : item.name}</option>
    ));

    const minimum_working_hours_per_block_active = magic_plan.minimum_working_hours_per_block.active;
    const maximum_working_hours_per_block_active = magic_plan.maximum_working_hours_per_block.active;
    const minimum_rest_hours_between_blocks_active = magic_plan.minimum_rest_hours_between_blocks.active;
    const maximum_rest_hours_between_blocks_active = magic_plan.maximum_rest_hours_between_blocks.active;
    const rest_window_between_blocks_active = magic_plan.rest_window_between_blocks?.active;
    const rest_window_between_blocks_value = magic_plan.rest_window_between_blocks?.value || { start: "", end: "" };
    const number_of_blocks_active = magic_plan.number_of_blocks.active;
    const max_weekly_days_with_multiple_blocks_active = magic_plan.max_weekly_days_with_multiple_blocks.active;
    const min_resthours_between_workdays_active = magic_plan.min_resthours_between_workdays.active;
    const min_resthours_between_shifts_active = magic_plan.min_resthours_between_shifts.active;
    const max_resthours_between_shifts_active = magic_plan.max_resthours_between_shifts.active;
    const min_weekly_restdays_active = magic_plan.min_weekly_restdays.active;
    const minimum_free_weekends_active = magic_plan.minimum_free_weekends.active;
    const minimum_weekly_consecutive_rest_hours_active = magic_plan.minimum_weekly_consecutive_rest_hours.active;
    const min_consecutive_restdays_active = magic_plan.min_consecutive_restdays.active;
    const min_consecutive_rest_hours_active = magic_plan.min_consecutive_rest_hours.active;
    const max_consecutive_restdays_active = magic_plan.max_consecutive_restdays.active;
    const min_consecutive_workdays_active = magic_plan.min_consecutive_workdays.active;
    const max_working_hours_between_restdays_active = magic_plan.max_working_hours_between_restdays.active;
    const minimum_daily_hours_active = magic_plan.minimum_daily_hours.active;
    const maximum_daily_hours_active = magic_plan.maximum_daily_hours.active;

    const extra_hours_allowed = magic_plan.extra_hours_allowed;
    const over_assign_needs = magic_plan.over_assign_needs;
    const balance_working_hours = magic_plan.balance_working_hours;
    const balance_shifts_by_employee = magic_plan.balance_shifts_by_employee;
    const balance_shifts_by_employee_high_priority = magic_plan.balance_shifts_by_employee_high_priority;
    const different_consecutive_shifts = magic_plan.different_consecutive_shifts;
    const max_consecutive_same_shift_active = magic_plan.max_consecutive_same_shift.active;
    const minimize_shift_changes = magic_plan.minimize_shift_changes;
    const different_shifts_across_break = magic_plan.different_shifts_across_break;
    const maximize_skills = magic_plan.maximize_skills;
    const ubiquitous_employees = magic_plan.ubiquitous_employees;
    const single_ubication_per_day = magic_plan.single_ubication_per_day;
    const maximize_function_priorities = magic_plan.maximize_function_priorities;
    const maximize_shift_priorities = magic_plan.maximize_shift_priorities;
    const forbid_unassigned_shifts = magic_plan.forbid_unassigned_shifts;
    const avoid_unassigned_shifts = magic_plan.avoid_unassigned_shifts;
    const single_function_per_block = magic_plan.single_function_per_block;
    const single_ubication_per_block = magic_plan.single_ubication_per_block;
    const selectedGroup = this.getSelectedGroup();
    const groupEmployees = Array.isArray(selectedGroup?.employees) ? selectedGroup.employees : [];
    const planningEmployees = Array.isArray(magic_plan.employees) ? magic_plan.employees : [];
    const objectiveAssignmentsByEmployee = this.buildAssignmentIndex(objectiveAssignments);
    const balanceAssignmentsByEmployee = this.buildAssignmentIndex(balanceAssignments);
    const planEmployeesById = new Map(
      (planAssignments || [])
        .map((assignment) => {
          const employee = assignment?.employee;
          const employeeId = this.normalizeEmployeeId(employee?.id);
          return employeeId && employee ? [employeeId, employee] : null;
        })
        .filter(Boolean)
    );

    const groupEmployeesById = new Map(
      groupEmployees
        .map((employee) => [this.normalizeEmployeeId(employee?.id), employee])
        .filter(([employeeId]) => !!employeeId)
    );
    const planningEmployeesById = new Map(
      planningEmployees
        .map((employee) => [this.normalizeEmployeeId(employee?.id), employee])
        .filter(([employeeId]) => !!employeeId)
    );

    const employeeIdsFromSources = new Set([
      ...groupEmployeesById.keys(),
      ...planningEmployeesById.keys(),
      ...Object.keys(objectiveAssignmentsByEmployee)
        .map((id) => this.normalizeEmployeeId(id))
        .filter((employeeId) => !!employeeId),
      ...Object.keys(balanceAssignmentsByEmployee)
        .map((id) => this.normalizeEmployeeId(id))
        .filter((employeeId) => !!employeeId),
    ]);

    const planEmployeeIds = this.getUniquePlanAssignmentEmployeeIds(planAssignments);
    const sourceEmployeeIds = [...employeeIdsFromSources];
    const employeeIdsForTargetHours = planEmployeeIds.length > 0
      ? planEmployeeIds
      : sourceEmployeeIds;

    const mergedPlanningEmployees = employeeIdsForTargetHours.map((employeeId) => {
      const groupEmployee = groupEmployeesById.get(employeeId) || {};
      const planEmployee = planEmployeesById.get(employeeId) || {};
      const planningEmployee = planningEmployeesById.get(employeeId) || {};
      const objectiveAssignment = objectiveAssignmentsByEmployee[employeeId] || {};
      const balanceAssignment = balanceAssignmentsByEmployee[employeeId] || {};

      return {
        ...groupEmployee,
        ...planEmployee,
        ...objectiveAssignment.employee,
        ...balanceAssignment.employee,
        ...planningEmployee,
        id: employeeId,
        target_hours: this.roundToTwoDecimals(planningEmployee.target_hours) ?? this.roundToTwoDecimals(objectiveAssignment.value) ?? 0,
        balance_until_today: this.toNumber(planningEmployee.balance_until_today) ?? this.toNumber(balanceAssignment.value) ?? 0,
      };
    });

    const planEmployeesOrder = new Map();
    planEmployeeIds.forEach((employeeId, index) => {
      planEmployeesOrder.set(employeeId, index);
    });

    const baseEmployees = [...mergedPlanningEmployees].sort((a, b) => {
      const orderA = planEmployeesOrder.get(a.id);
      const orderB = planEmployeesOrder.get(b.id);
      const aInPlanOrder = typeof orderA === "number";
      const bInPlanOrder = typeof orderB === "number";

      if (aInPlanOrder && bInPlanOrder) {
        return orderA - orderB;
      }

      if (aInPlanOrder) return -1;
      if (bInPlanOrder) return 1;

      return this.getEmployeeBalanceHours(b) - this.getEmployeeBalanceHours(a);
    });
    const { targetHoursSort } = this.state;
    const sortedEmployees = targetHoursSort.key
      ? [...baseEmployees].sort((a, b) => {
        const direction = targetHoursSort.direction === "asc" ? 1 : -1;

        if (targetHoursSort.key === "employee") {
          return (
            this.getEmployeeName(a).localeCompare(
              this.getEmployeeName(b),
              "es",
              { sensitivity: "base" }
            ) * direction
          );
        }

        if (targetHoursSort.key === "balance") {
          return (
            (this.getEmployeeBalanceHours(a) - this.getEmployeeBalanceHours(b)) *
            direction
          );
        }

        if (targetHoursSort.key === "target_hours") {
          return (
            (this.getEmployeeTargetHours(a) - this.getEmployeeTargetHours(b)) *
            direction
          );
        }

        return 0;
      })
      : baseEmployees;
    const objectiveKpis = this.getObjectiveKpisFromEmployees(sortedEmployees, assigmentsData);
    const iterativeValue = magic_plan.iterative_calculation_in_days;
    let targetHoursLabel;
    if (!iterativeValue.active) {
      targetHoursLabel = i18next.t('magicPlan.targetHoursLabel');
    } else if (iterativeValue.value === 27) {
      targetHoursLabel = i18next.t('magicPlan.targetHoursFourWeeks');
    } else {
      targetHoursLabel = i18next.t('magicPlan.targetHoursWeekly');
    }

    return (
      <div className="plan-magic-restrictions">
        <h2 className="plan-magic-restrictions__title">{this.getNavTitle()}</h2>
        {magic_plan.group_id && this.state.activeTabKey !== "horas-objetivo" && this.hasManualTargetHoursOverrides() ? (
          <TargetHoursOverridesNotice
            actionLabel={i18next.t('magicPlan.goToTab', { tab: targetHoursLabel })}
            onAction={() => this.handleTabSelect("horas-objetivo")}
          >
            {i18next.t('magicPlan.manualOverridesPre')} <strong>{targetHoursLabel}</strong> {i18next.t('magicPlan.manualOverridesPost')}
          </TargetHoursOverridesNotice>
        ) : null}
        <Tab.Container activeKey={this.state.activeTabKey} className="tabs plan-magic-tabs">
          <div className="scrollable-container" ref={this.contentScrollRef}>
            <Tab.Content>
              <Tab.Pane eventKey="config">
                {this.renderConfigPanel()}
              </Tab.Pane>
              <Tab.Pane eventKey="horas">
                <div className="tab-group">
                  <div className="switch-group">
                    <h3 className="plan-magic-section-title">{i18next.t('magicPlan.tabHours')}</h3>
                    <FormGroup className="form-group form-group-flex form-row-switch form-group-main" controlId="extra_hours_allowed">
                      <FormLabel>{i18next.t('magicPlan.labelExtraHours')}</FormLabel>
                      <Switch controlId="extra_hours_allowed" isOn={extra_hours_allowed} handleToggle={this.handleChange} />
                    </FormGroup>
                    <FormGroup className="form-group form-group-flex form-row-switch form-group-main" controlId="over_assign_needs">
                      <FormLabel>{i18next.t('magicPlan.labelOverAssign')}</FormLabel>
                      <Switch controlId="over_assign_needs" isOn={over_assign_needs} handleToggle={this.handleChange} />
                    </FormGroup>
                    {this.renderRestrictionValueRow({
                      fieldKey: "min_consecutive_workdays",
                      label: i18next.t("magicPlan.labelMinConsecutiveWorkdays"),
                      unit: "magicPlan.unitDays",
                      active: min_consecutive_workdays_active,
                    })}
                    {this.renderRestrictionValueRow({
                      fieldKey: "max_working_hours_between_restdays",
                      label: i18next.t("magicPlan.labelMaxHoursBetweenRest"),
                      unit: "magicPlan.unitHours",
                      active: max_working_hours_between_restdays_active,
                    })}
                    {this.renderRestrictionBlock("magicPlan.groupDailyHours", (
                      <>
                        {this.renderRestrictionValueRow({
                          fieldKey: "minimum_daily_hours",
                          label: i18next.t("magicPlan.labelMinHours"),
                          unit: "magicPlan.unitHours",
                          active: minimum_daily_hours_active,
                        })}
                        {this.renderRestrictionValueRow({
                          fieldKey: "maximum_daily_hours",
                          label: i18next.t("magicPlan.labelMaxHours"),
                          unit: "magicPlan.unitHours",
                          active: maximum_daily_hours_active,
                        })}
                      </>
                    ))}
                  </div>

                  <div className="switch-group">
                    <h3 className="plan-magic-section-title">{i18next.t('magicPlan.sectionWeeklyRotation')}</h3>
                    <div className="switch-with-input">
                      <FormGroup className="form-group form-group-flex form-row-switch form-group-main">
                        <FormLabel htmlFor="different_weekly_shifts">{i18next.t('magicPlan.labelSameShiftWeek')}</FormLabel>
                        {different_weekly_shifts && magic_plan.different_weekly_shifts.start && magic_plan.different_weekly_shifts.end && (
                          <div className="time-display">
                            <FormControl type="text" value={`${convertFromUTC(magic_plan.different_weekly_shifts.start)} - ${convertFromUTC(magic_plan.different_weekly_shifts.end)}`} readOnly style={{ backgroundColor: '#f8f9fa', cursor: 'default', textAlign: 'center' }} />
                          </div>
                        )}
                        <Switch
                          controlId="different_weekly_shifts"
                          isOn={different_weekly_shifts}
                          handleToggle={this.handleDifferentWeeklyShiftsToggle}
                        />
                      </FormGroup>
                    </div>

                    <div className="switch-with-input">
                      <FormGroup className="form-group form-group-flex form-row-switch form-group-main">
                        <FormLabel htmlFor="different_weekly_shifts_until_friday">{i18next.t('magicPlan.labelSameShiftFriday')}</FormLabel>
                        {different_weekly_shifts_until_friday && magic_plan.different_weekly_shifts_until_friday.start && magic_plan.different_weekly_shifts_until_friday.end && (
                          <div className="time-display">
                            <FormControl type="text" value={`${convertFromUTC(magic_plan.different_weekly_shifts_until_friday.start)} - ${convertFromUTC(magic_plan.different_weekly_shifts_until_friday.end)}`} readOnly style={{ backgroundColor: '#f8f9fa', cursor: 'default', textAlign: 'center' }} />
                          </div>
                        )}
                        <Switch
                          controlId="different_weekly_shifts_until_friday"
                          isOn={different_weekly_shifts_until_friday}
                          handleToggle={this.handleDifferentWeeklyShiftsUntilFridayToggle}
                        />
                      </FormGroup>
                    </div>

                    <FormGroup className="form-group form-group-flex form-row-switch form-group-main" controlId="different_consecutive_shifts">
                      <FormLabel>{i18next.t('magicPlan.labelDifferentConsecutive')}</FormLabel>
                      <Switch controlId="different_consecutive_shifts" isOn={different_consecutive_shifts} handleToggle={this.handleChange} />
                    </FormGroup>

                    <FormGroup className="form-group form-group-flex form-row-switch form-group-main" controlId="minimize_shift_changes">
                      <FormLabel>{i18next.t('magicPlan.labelMinimizeChanges')}</FormLabel>
                      <Switch controlId="minimize_shift_changes" isOn={minimize_shift_changes} handleToggle={this.handleChange} />
                    </FormGroup>

                    {this.renderRestrictionValueRow({
                      fieldKey: "max_consecutive_same_shift",
                      label: i18next.t("magicPlan.labelMaxConsecutiveSameShift"),
                      unit: "magicPlan.unitTimes",
                      active: max_consecutive_same_shift_active,
                      unitCount: parseInt(magic_plan.max_consecutive_same_shift.value, 10) || 0,
                    })}

                    <div className="shift-weekly-limit-add">
                      <FormLabel className="shift-weekly-limit-add__label">
                        {i18next.t('magicPlan.labelMaxWeeklyShiftAssignments')}
                      </FormLabel>
                      <div className="shift-weekly-limit-add__row">
                        <FormControl
                          onChange={this.handleShiftWeeklyLimitChange}
                          value={this.state.new_shift_weekly_limit.shift_id}
                          id="new_shift_weekly_limit_shift"
                          placeholder={i18next.t('magicPlan.selectShift')}
                          isInvalid={this.checkValidField("shift_weekly_limit")}
                          as="select"
                          className="shift-weekly-limit-add__shift"
                        >
                          <option value="">{i18next.t('magicPlan.selectShiftForWeeklyLimit')}</option>
                          {optionShiftWeeklyLimitList}
                        </FormControl>
                        <FormGroup className="time-display time-display--compact" controlId="new_shift_weekly_limit_value">
                          <FormControl
                            type="text"
                            id="new_shift_weekly_limit_value"
                            value={this.state.new_shift_weekly_limit.max_weekly_shift_assignments}
                            onChange={this.handleShiftWeeklyLimitChange}
                            placeholder="0"
                            isInvalid={this.checkValidField("shift_weekly_limit_value")}
                          />
                          <span className="time-display__unit">
                            {i18next.t('magicPlan.unitTimes', { count: parseInt(this.state.new_shift_weekly_limit.max_weekly_shift_assignments, 10) || 0 })}
                          </span>
                        </FormGroup>
                        <button type="button" onClick={this.addShiftWeeklyLimit} className="btn btn-primary shift-weekly-limit-add__btn">
                          {i18next.t('common.add')}
                        </button>
                      </div>
                    </div>
                    {shift_weekly_limits.length !== 0 && (
                      <ul className="list-tags shift-weekly-limit-list">{this.getSelectedShiftWeeklyLimits()}</ul>
                    )}
                  </div>

                  <div className="switch-group">
                    <h3 className="plan-magic-section-title">{i18next.t('magicPlan.sectionBalance')}</h3>
                    <FormGroup className="form-group form-group-flex form-row-switch form-group-main" controlId="balance_working_hours">
                      <FormLabel>{i18next.t('magicPlan.labelBalanceHours')}</FormLabel>
                      <Switch controlId="balance_working_hours" isOn={balance_working_hours} handleToggle={this.handleChange} />
                    </FormGroup>
                    <FormGroup className="form-group form-group-flex form-row-switch form-group-main" controlId="balance_shifts_by_employee">
                      <FormLabel>{i18next.t('magicPlan.labelBalanceShifts')}</FormLabel>
                      <Switch controlId="balance_shifts_by_employee" isOn={balance_shifts_by_employee} handleToggle={this.handleChange} />
                    </FormGroup>
                    {isBalanceShiftsHighPriorityVisible(magic_plan) && (
                      <FormGroup className="form-group form-group-flex form-row-switch form-group-main form-row-switch--nested" controlId="balance_shifts_by_employee_high_priority">
                        <FormLabel>{i18next.t('magicPlan.labelBalanceShiftsHighPriority')}</FormLabel>
                        <Switch controlId="balance_shifts_by_employee_high_priority" isOn={balance_shifts_by_employee_high_priority} handleToggle={this.handleChange} />
                      </FormGroup>
                    )}
                    <FormGroup className="form-group form-group-flex form-row-switch form-group-main" controlId="maximize_shift_priorities">
                      <FormLabel>{i18next.t('magicPlan.labelMaximizeShiftPriorities')}</FormLabel>
                      <Switch controlId="maximize_shift_priorities" isOn={maximize_shift_priorities} handleToggle={this.handleChange} />
                    </FormGroup>
                    <FormGroup className="form-group form-group-flex select-input" controlId="new_high_load_shift">
                      <FormLabel>{i18next.t('magicPlan.labelPrioritizeHighLoadShifts')}</FormLabel>
                      <div className="form-box-wrap">
                        <div className="form-box">
                          <FormControl onChange={this.handleAddHighLoadShiftChange} value={this.state.new_high_load_shift} placeholder={i18next.t('magicPlan.selectShift')} isInvalid={this.checkValidField("high_load_shift_ids")} as="select" disabled={!maximize_shift_priorities}>
                            <option value="">{i18next.t('magicPlan.selectShiftForHighLoad')}</option>
                            {optionShiftList}
                          </FormControl>
                          {this.state.selected_high_load_shifts.length !== 0 && (
                            <ul className="list-tags">{this.getSelectedHighLoadShifts()}</ul>
                          )}
                        </div>
                        <button type="button" onClick={this.addHighLoadShift} className="btn btn-primary" disabled={!maximize_shift_priorities}>{i18next.t('common.add')}</button>
                      </div>
                    </FormGroup>
                  </div>

                </div>

              </Tab.Pane>

              <Tab.Pane eventKey="horas-objetivo" className="plan-target-hours">
                  {!magic_plan.group_id ? (
                    <div className="plan-target-hours-empty-state">
                      {i18next.t('magicPlan.targetHoursEmpty')}
                    </div>
                  ) : (
                    <>
                      <div className="plan-target-hours-cards">
                        <div className="plan-target-hours-card">
                          <span className="plan-target-hours-card__label">
                            {targetHoursLabel}
                            <OverlayTrigger
                              placement="top"
                              overlay={(
                                <Tooltip id="tooltip-kpi-target-hours">
                                  {i18next.t('magicPlan.tooltipTargetHours')}
                                </Tooltip>
                              )}
                            >
                              <button type="button" className="plan-target-hours-card__help" aria-label={i18next.t('magicPlan.ariaTargetHours')}>
                                <FiInfo size={12} />
                              </button>
                            </OverlayTrigger>
                          </span>
                          <span className="plan-target-hours-card__value">{this.formatHours(objectiveKpis.targetHours)}<small>h</small></span>
                        </div>
                        <div className="plan-target-hours-card">
                          <span className="plan-target-hours-card__label">
                            {i18next.t('magicPlan.kpiStaffingLabel')}
                            <OverlayTrigger
                              placement="top"
                              overlay={(
                                <Tooltip id="tooltip-kpi-needed-hours">
                                  {i18next.t('magicPlan.tooltipStaffing')}
                                </Tooltip>
                              )}
                            >
                              <button type="button" className="plan-target-hours-card__help" aria-label={i18next.t('magicPlan.ariaStaffing')}>
                                <FiInfo size={12} />
                              </button>
                            </OverlayTrigger>
                          </span>
                          <span className="plan-target-hours-card__value">{this.formatHours(objectiveKpis.neededHours)}<small>h</small></span>
                        </div>
                        <div className="plan-target-hours-card">
                          <span className="plan-target-hours-card__label">
                            {i18next.t('magicPlan.kpiDiffLabel')}
                            <OverlayTrigger
                              placement="top"
                              overlay={(
                                <Tooltip id="tooltip-kpi-difference-hours">
                                  {i18next.t('magicPlan.tooltipDiff')}
                                </Tooltip>
                              )}
                            >
                              <button type="button" className="plan-target-hours-card__help" aria-label={i18next.t('magicPlan.ariaDiff')}>
                                <FiInfo size={12} />
                              </button>
                            </OverlayTrigger>
                          </span>
                          <span className={`plan-target-hours-card__value ${objectiveKpis.differenceHours >= 0 ? "plan-target-hours-card__value--positive" : "plan-target-hours-card__value--negative"}`}>
                            {objectiveKpis.differenceHours >= 0 ? "+" : "-"}
                            {this.formatHours(Math.abs(objectiveKpis.differenceHours))}
                            <small>h</small>
                          </span>
                        </div>
                      </div>

                      <p>{i18next.t('magicPlan.editTargetHoursHint')}</p>

                      <div className="plan-target-hours-table">
                        <div className="plan-target-hours-table__head">
                          <span>
                            <button
                              type="button"
                              className="btn btn-link p-0 text-reset"
                              style={{ textDecoration: "none", fontWeight: "inherit" }}
                              onClick={() => this.handleTargetHoursSort("employee")}
                            >
                              {i18next.t('magicPlan.colEmployee')}
                              {this.getTargetHoursSortIndicator("employee")}
                            </button>
                          </span>
                          <span>
                            <button
                              type="button"
                              className="btn btn-link p-0 text-reset"
                              style={{ textDecoration: "none", fontWeight: "inherit" }}
                              onClick={() => this.handleTargetHoursSort("balance")}
                            >
                              {i18next.t('magicPlan.colBalanceToday')}
                              {this.getTargetHoursSortIndicator("balance")}
                            </button>
                          </span>
                          <span className="plan-target-hours-table__head-with-help">
                            <button
                              type="button"
                              className="btn btn-link p-0 text-reset"
                              style={{ textDecoration: "none", fontWeight: "inherit" }}
                              onClick={() => this.handleTargetHoursSort("target_hours")}
                            >
                              {targetHoursLabel}
                              {this.getTargetHoursSortIndicator("target_hours")}
                            </button>
                          </span>
                        </div>
                        <div className="plan-target-hours-table__body">
                          {sortedEmployees.length > 0 ? sortedEmployees.map((employee) => (
                            <div key={employee.id} className="plan-target-hours-table__row">
                              <div className="plan-target-hours-employee">
                                <div className="plan-target-hours-employee__avatar">
                                  {employeeAvatars[employee.id] ? (
                                    <img
                                      src={employeeAvatars[employee.id]}
                                      alt={this.getEmployeeName(employee)}
                                      className="plan-target-hours-employee__avatar-image"
                                    />
                                  ) : (
                                    this.getEmployeeInitials(employee)
                                  )}
                                </div>
                                <span>{this.getEmployeeName(employee)}</span>
                              </div>
                              <div className={`plan-target-hours-balance ${this.getEmployeeBalanceHours(employee) < 0 ? "is-negative" : ""}`}>
                                {this.formatSignedHours(this.getEmployeeBalanceHours(employee))}
                              </div>
                              <div className="plan-target-hours-target">
                                <InputGroup>
                                  <FormControl
                                    type="text"
                                    inputMode="decimal"
                                    value={this.getEmployeeTargetHoursDisplay(employee)}
                                    onChange={(event) => this.handleEmployeeTargetHoursChange(employee.id, event.target.value)}
                                  />
                                  <InputGroup.Text>h</InputGroup.Text>
                                </InputGroup>
                                <span className="plan-target-hours-target__account-line">
                                  {i18next.t('magicPlan.accountPeriodLabel', {
                                    period: !magic_plan.iterative_calculation_in_days?.active
                                      ? i18next.t('magicPlan.period')
                                      : magic_plan.iterative_calculation_in_days.value === 27
                                        ? i18next.t('magicPlan.fourWeekly')
                                        : i18next.t('magicPlan.weekly')
                                  })}{" "}
                                  {this.formatHours(
                                    this.roundToTwoDecimals(
                                      (objectiveAssignmentsByEmployee[employee.id]?.value ?? 0)
                                      / (magic_plan.iterative_calculation_in_days?.active ? this.getPlanningBlocksCount() : 1)
                                    ) ?? 0,
                                    2,
                                    2
                                  )}
                                  h
                                </span>
                              </div>
                            </div>
                          )) : (
                            <div className="plan-target-hours-empty-state">
                              {assignmentsLoading
                                ? i18next.t('magicPlan.loadingTargetHours')
                                : i18next.t('magicPlan.noEmployeesForPeriod')}
                            </div>
                          )}
                        </div>
                      </div>
                    </>
                  )}
                </Tab.Pane>

              {magic_plan.fraction_shifts && (
                <Tab.Pane eventKey="fracciones" className="plan-fracciones">
                  <div className="tab-group">
                    <div className="switch-group">
                      <h3 className="plan-magic-section-title">{i18next.t('magicPlan.sectionBlocks')}</h3>
                      {this.renderRestrictionBlock("magicPlan.groupBlockHours", (
                        <>
                          {this.renderRestrictionValueRow({
                            fieldKey: "minimum_working_hours_per_block",
                            label: i18next.t("magicPlan.labelMinHours"),
                            unit: "magicPlan.unitHours",
                            active: minimum_working_hours_per_block_active,
                          })}
                          {this.renderRestrictionValueRow({
                            fieldKey: "maximum_working_hours_per_block",
                            label: i18next.t("magicPlan.labelMaxHours"),
                            unit: "magicPlan.unitHours",
                            active: maximum_working_hours_per_block_active,
                          })}
                        </>
                      ))}
                      {this.renderRestrictionBlock("magicPlan.groupRestBetweenBlocks", (
                        <>
                          {this.renderRestrictionValueRow({
                            fieldKey: "minimum_rest_hours_between_blocks",
                            label: i18next.t("magicPlan.labelMinHours"),
                            unit: "magicPlan.unitHours",
                            active: minimum_rest_hours_between_blocks_active,
                          })}
                          {this.renderRestrictionValueRow({
                            fieldKey: "maximum_rest_hours_between_blocks",
                            label: i18next.t("magicPlan.labelMaxHours"),
                            unit: "magicPlan.unitHours",
                            active: maximum_rest_hours_between_blocks_active,
                          })}
                        </>
                      ))}

                      <div className="switch-with-input">
                        <FormGroup className="form-group form-group-flex form-row-switch form-group-main" controlId="rest_window_between_blocks.active">
                          <FormLabel>{i18next.t('magicPlan.labelRestWindow')}</FormLabel>
                          {rest_window_between_blocks_active ? (
                            <FormGroup className="time-display time-display--rest-window" controlId="rest_window_between_blocks.value">
                              <TimeInputWithArrows
                                id="rest_window_between_blocks_start"
                                value={rest_window_between_blocks_value.start}
                                className="hours-restriction-time__input"
                                placeholder="hh:mm"
                                showDecorators={false}
                                nextInputId="rest_window_between_blocks_end"
                                onChange={(event) => this.handleRestWindowBetweenBlocksTimeChange("start", event.target.value)}
                              />
                              <span className="time-display__separator">-</span>
                              <TimeInputWithArrows
                                id="rest_window_between_blocks_end"
                                value={rest_window_between_blocks_value.end}
                                className="hours-restriction-time__input"
                                placeholder="hh:mm"
                                showDecorators={false}
                                onChange={(event) => this.handleRestWindowBetweenBlocksTimeChange("end", event.target.value)}
                              />
                            </FormGroup>
                          ) : null}
                          <Switch controlId="rest_window_between_blocks.active" isOn={rest_window_between_blocks_active} handleToggle={this.handleChange} />
                        </FormGroup>
                      </div>

                      {this.renderRestrictionValueRow({
                        fieldKey: "number_of_blocks",
                        label: i18next.t("magicPlan.labelMaxBlocks"),
                        unit: "magicPlan.unitBlocks",
                        active: number_of_blocks_active,
                      })}
                      {this.renderRestrictionValueRow({
                        fieldKey: "max_weekly_days_with_multiple_blocks",
                        label: i18next.t("magicPlan.labelMaxWeeklyBlocks"),
                        unit: "magicPlan.unitShifts",
                        active: max_weekly_days_with_multiple_blocks_active,
                      })}
                    </div>

                    <div className="switch-group">
                      <h3 className="plan-magic-section-title">{i18next.t('magicPlan.tabFunctionsUbications')}</h3>
                      <FormGroup className="form-group form-group-flex form-row-switch form-group-main" controlId="single_function_per_block">
                        <FormLabel>{i18next.t('magicPlan.labelSingleFunctionPerBlock')}</FormLabel>
                        <Switch controlId="single_function_per_block" isOn={single_function_per_block} handleToggle={this.handleChange} />
                      </FormGroup>
                      <FormGroup className="form-group form-group-flex form-row-switch form-group-main" controlId="single_ubication_per_block">
                        <FormLabel>{i18next.t('magicPlan.labelSingleUbicationPerBlock')}</FormLabel>
                        <Switch controlId="single_ubication_per_block" isOn={single_ubication_per_block} handleToggle={this.handleChange} />
                      </FormGroup>
                    </div>
                  </div>
                </Tab.Pane>
              )}

              <Tab.Pane eventKey="descansos" className="plan-rules">
                <div className="tab-group">
                  <div className="switch-group">
                    <h3 className="plan-magic-section-title">{i18next.t('magicPlan.sectionRestBetweenShifts')}</h3>
                    {this.renderRestrictionValueRow({
                      fieldKey: "min_resthours_between_workdays",
                      label: i18next.t("magicPlan.labelMinRestBetweenWorkdays"),
                      unit: "magicPlan.unitHours",
                      active: min_resthours_between_workdays_active,
                    })}
                    {!magic_plan.fraction_shifts && this.renderRestrictionBlock("magicPlan.groupRestBetweenShifts", (
                      <>
                        {this.renderRestrictionValueRow({
                          fieldKey: "min_resthours_between_shifts",
                          label: i18next.t("magicPlan.labelMinHours"),
                          unit: "magicPlan.unitHours",
                          active: min_resthours_between_shifts_active,
                        })}
                        {this.renderRestrictionValueRow({
                          fieldKey: "max_resthours_between_shifts",
                          label: i18next.t("magicPlan.labelMaxHours"),
                          unit: "magicPlan.unitHours",
                          active: max_resthours_between_shifts_active,
                        })}
                      </>
                    ))}
                    <FormGroup className="form-group form-group-flex form-row-switch form-group-main" controlId="separated_restdays">
                      <FormLabel>{i18next.t('magicPlan.labelAvoidRestOverlap')}</FormLabel>
                      <Switch controlId="separated_restdays" isOn={magic_plan.separated_restdays} handleToggle={this.handleChange} />
                    </FormGroup>
                  </div>

                  <div className="switch-group">
                    <h3 className="plan-magic-section-title">{i18next.t('magicPlan.sectionWeeklyRest')}</h3>
                    {this.renderRestrictionValueRow({
                      fieldKey: "min_weekly_restdays",
                      label: i18next.t("magicPlan.labelMinWeeklyRestDays"),
                      unit: "magicPlan.unitDays",
                      active: min_weekly_restdays_active,
                    })}
                    {this.renderRestrictionValueRow({
                      fieldKey: "minimum_weekly_consecutive_rest_hours",
                      label: i18next.t("magicPlan.labelMinConsecutiveRestHours"),
                      unit: "magicPlan.unitHours",
                      active: minimum_weekly_consecutive_rest_hours_active,
                    })}
                    {this.renderRestrictionValueRow({
                      fieldKey: "minimum_free_weekends",
                      label: i18next.t("magicPlan.labelMinFreeWeekends"),
                      unit: "magicPlan.unitWeekends",
                      active: minimum_free_weekends_active,
                    })}
                  </div>

                  <div className="switch-group">
                    <h3 className="plan-magic-section-title">{i18next.t('magicPlan.sectionRestSequence')}</h3>
                    <FormGroup className="form-group form-group-flex form-row-switch form-group-main" controlId="different_shifts_across_break">
                      <FormLabel>{i18next.t('magicPlan.labelDifferentShiftsAcrossBreak')}</FormLabel>
                      <Switch controlId="different_shifts_across_break" isOn={different_shifts_across_break} handleToggle={this.handleChange} />
                    </FormGroup>
                    {this.renderRestrictionBlock("magicPlan.groupConsecutiveRestDays", (
                      <>
                        {this.renderRestrictionValueRow({
                          fieldKey: "min_consecutive_restdays",
                          label: i18next.t("magicPlan.labelMinimum"),
                          unit: "magicPlan.unitDays",
                          active: min_consecutive_restdays_active,
                        })}
                        {this.renderRestrictionValueRow({
                          fieldKey: "max_consecutive_restdays",
                          label: i18next.t("magicPlan.labelMaximum"),
                          unit: "magicPlan.unitDays",
                          active: max_consecutive_restdays_active,
                        })}
                      </>
                    ))}
                    {this.renderRestrictionValueRow({
                      fieldKey: "min_consecutive_rest_hours",
                      label: i18next.t("magicPlan.labelMinConsecutiveRestHoursAfterBlock"),
                      unit: "magicPlan.unitHours",
                      active: min_consecutive_rest_hours_active,
                    })}
                    <div className="switch-with-input">
                      <FormGroup className="form-group form-group-flex select-input" controlId="new_day_type">
                        <FormLabel>{i18next.t('magicPlan.labelAllowedRestDayTypes')}</FormLabel>
                        <div className="form-box-wrap">
                          <div className="form-box">
                            <FormControl onChange={this.handleAddChange} value={this.state.new_day_type} placeholder={i18next.t('magicPlan.selectFunction')} isInvalid={this.checkValidField("day_type_ids")} as="select">
                              <option value="">{i18next.t('magicPlan.selectDayType')}</option>
                              {optionDayTypeList}
                            </FormControl>
                            {this.state.selected_day_types.length === 0 ? (
                              <div className="row-adding-help">
                                <button className="btn-link" onClick={() => this.addBulkDayTypes(1, 5)}>{i18next.t('magicPlan.btnAddMonFri')}</button>
                                <button className="btn-link" onClick={() => this.addBulkDayTypes(1, 7)}>{i18next.t('magicPlan.btnAddMonSun')}</button>
                              </div>
                            ) : (
                              <ul className="list-tags">{this.getSelectedDayTypes()}</ul>
                            )}
                          </div>
                          <button type="button" onClick={this.addDayType} className="btn btn-primary">{i18next.t('common.add')}</button>
                        </div>
                      </FormGroup>
                    </div>

                    <FormGroup className="form-group form-group-flex select-input" controlId="new_shift">
                      <FormLabel>{i18next.t('magicPlan.labelRestAfterShifts')}</FormLabel>
                      <div className="form-box-wrap">
                        <div className="form-box">
                          <FormControl onChange={this.handleAddShiftChange} value={this.state.new_shift} placeholder={i18next.t('magicPlan.selectShift')} isInvalid={this.checkValidField("shift_ids")} as="select">
                            <option value="">{i18next.t('magicPlan.selectShiftForRest')}</option>
                            {optionShiftList}
                          </FormControl>
                          {this.state.selected_shifts.length !== 0 && (
                            <ul className="list-tags">{this.getSelectedShifts()}</ul>
                          )}
                        </div>
                        <button type="button" onClick={this.addShift} className="btn btn-primary">{i18next.t('common.add')}</button>
                      </div>
                    </FormGroup>
                  </div>
                </div>

              </Tab.Pane>

              <Tab.Pane eventKey="ubicaciones" className="plan-ubications">
                <div className="tab-group">
                  <div className="switch-group">
                    <h3 className="plan-magic-section-title">{i18next.t('magicPlan.sectionFunctions')}</h3>
                    <FormGroup className="form-group form-group-flex form-row-switch form-group-main" controlId="maximize_function_priorities">
                      <FormLabel>{i18next.t('magicPlan.labelMaximizePriority')}</FormLabel>
                      <Switch controlId="maximize_function_priorities" isOn={maximize_function_priorities} handleToggle={this.handleChange} />
                    </FormGroup>
                    <FormGroup className="form-group form-group-flex form-row-switch form-group-main" controlId="forbid_unassigned_shifts">
                      <FormLabel>{i18next.t('magicPlan.labelForbidUnassigned')}</FormLabel>
                      <Switch controlId="forbid_unassigned_shifts" isOn={forbid_unassigned_shifts} handleToggle={this.handleChange} />
                    </FormGroup>
                    <FormGroup className="form-group form-group-flex form-row-switch form-group-main" controlId="avoid_unassigned_shifts">
                      <FormLabel>{i18next.t('magicPlan.labelAvoidUnassigned')}</FormLabel>
                      <Switch controlId="avoid_unassigned_shifts" isOn={avoid_unassigned_shifts} handleToggle={this.handleChange} />
                    </FormGroup>
                  </div>
                  <div className="switch-group">
                    <h3 className="plan-magic-section-title">{i18next.t('magicPlan.sectionUbications')}</h3>
                    <FormGroup className="form-group form-group-flex form-row-switch form-group-main" controlId="maximize_skills">
                      <FormLabel>{i18next.t('magicPlan.labelMaximizeSkills')}</FormLabel>
                      <Switch controlId="maximize_skills" isOn={maximize_skills} handleToggle={this.handleChange} />
                    </FormGroup>
                    <FormGroup className="form-group form-group-flex form-row-switch form-group-main" controlId="ubiquitous_employees">
                      <FormLabel>{i18next.t('magicPlan.labelMultipleUbications')}</FormLabel>
                      <Switch controlId="ubiquitous_employees" isOn={ubiquitous_employees} handleToggle={this.handleChange} />
                    </FormGroup>
                    <FormGroup className="form-group form-group-flex form-row-switch form-group-main" controlId="single_ubication_per_day">
                      <FormLabel>{i18next.t('magicPlan.labelSingleUbicationPerDay')}</FormLabel>
                      <Switch controlId="single_ubication_per_day" isOn={single_ubication_per_day} handleToggle={this.handleChange} />
                    </FormGroup>
                  </div>

                </div>
              </Tab.Pane>
            </Tab.Content>
          </div>
        </Tab.Container>
      </div>
    );
  };

  render() {
    return (
      <form onSubmit={this.handleSubmit}>
        <SimpleModal
          isOpen={true}
          onClose={this.props.handleClose}
          className="magic-modal"
        >
          <div className="plan-magic-shell">
            <div className={`plan-magic-shell__body${this.state.configurationLoaded ? "" : " is-loading-params"}`}>
              <aside className="plan-magic-shell__nav">{this.renderNav()}</aside>
              <section className="plan-magic-shell__content">{this.renderRight()}</section>
            </div>
            {this.renderFooter()}
          </div>
        </SimpleModal>

        <TimeConfigModal
          isOpen={this.state.show_time_modal && this.state.active_modal_type === 'weekly'}
          onClose={this.handleTimeModalCancel}
          onAccept={this.handleTimeModalAccept}
          tempTimeConfig={this.state.temp_time_config}
          onTimeChange={this.handleTimeConfigChange}
          title={i18next.t('magicPlan.labelSameShiftWeek')}
        />
        <TimeConfigModal
          isOpen={this.state.show_time_modal && this.state.active_modal_type === 'friday'}
          onClose={this.handleTimeModalFridayCancel}
          onAccept={this.handleTimeModalFridayAccept}
          tempTimeConfig={this.state.temp_time_config_friday}
          onTimeChange={this.handleTimeConfigFridayChange}
          title={i18next.t('magicPlan.labelSameShiftFriday')}
        />
      </form>
    );
  }
}

const mapStateToProps = (reducers) => ({
  ...reducers.groupsReducer,
  ...reducers.calendarsReducer,
  ...reducers.shiftsReducer,
  loading: reducers.groupsReducer.loading,
  user: reducers.authReducer.user,
});

const mapDispatchToProps = (dispatch) => ({
  getSelectableDayTypes: () => dispatch(calendarsActions.getSelectableDayTypes()),
});

export default connect(mapStateToProps, mapDispatchToProps)(withRouter(PlanMagicModalV2));


